import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import {
  custodianAPI,
  secondaryTransfersAPI,
  authAPI,
} from "../../services/api.js";
import axios from "axios";
import {
  targetTypeInfo,
  schemaFormatters,
  normalizeTargetType,
  formatActor,
  renderTargetDetails,
  exportAllAuditLogsCsv,
  formatValue,
} from "../../utils/auditUtils";
import {
  FileText,
  Download,
  Calendar,
  BarChart3,
  Users,
  Shield,
  Activity,
  DollarSign,
  Search,
  Filter,
  Eye,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertTriangle,
  Database,
  TrendingUp,
  X,
} from "lucide-react";

const slugify = (value) => {
  if (!value) {
    return "report";
  }
  return (
    value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "") || "report"
  );
};

const formatLabel = (key) => {
  if (!key) return "";
  return key
    .toString()
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatPrimitive = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "number")
    return Number.isFinite(value) ? value.toString() : "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value))
    return value.map((item) => formatPrimitive(item)).join("; ");
  if (typeof value === "object") return JSON.stringify(value);
  return value.toString();
};

const DAILY_POSITION_COLUMNS = [
  { key: "normalMasterNo", label: "Normal Master No" },
  { key: "normalMasterName", label: "Normal Master Name" },
  { key: "masterNo", label: "Master No" },
  { key: "masterName", label: "Master Name" },
  { key: "custodyAccountNo", label: "Custody Account No" },
  { key: "custodyAccountName", label: "Custody Account Name" },
  { key: "isin", label: "ISIN" },
  { key: "scanCode", label: "Scan Code" },
  { key: "securityCode", label: "Security Code" },
  { key: "securityDescription", label: "Security Description" },
  { key: "securityType", label: "Security Type" },
  { key: "securityStatus", label: "Security Status" },
  { key: "productType", label: "Bond Type" },
  { key: "locationCode", label: "Location Code" },
  { key: "currency", label: "Currency" },
  { key: "accountType", label: "Account Type" },
  { key: "scriplessFlag", label: "Scripless Flag" },
  { key: "maturityDate", label: "Maturity Date" },
  { key: "positionDate", label: "Position Date" },
  { key: "safekeepingPosition", label: "Safekeeping Position" },
  { key: "walletNo", label: "Wallet No" },
];

// Transaction File Columns mapped to specification (comments show original field name mapping)
const TRANSACTION_FILE_COLUMNS = [
  { key: "masterAccountNo", label: "Master Account No" }, // MASTER ACCOUNT (Custodian Bank Account No)
  { key: "masterAccountName", label: "Master Account Name" }, // MASTER ACCOUNT NAME (Custodian Name)
  { key: "custodyAccountNo", label: "Custody Account No" }, // ACCOUNT (Sub-Investor Bank Account No)
  { key: "custodyAccountName", label: "Custody Account Name" }, // ACCOUNT NAME (Sub-Investor Name)
  { key: "securityCode", label: "Security Code" }, // SECURITY CODE (Stock Code)
  { key: "securityName", label: "Security Name" }, // SECURITY NAME (Stock Name)
  { key: "securityLocation", label: "Security Location" }, // SECURITY LOCATION (Stock Location)
  { key: "currency", label: "Currency" }, // CURRENCY
  { key: "tradeDate", label: "Trade Date" }, // TRADE DATE (Trade Date)
  { key: "settlementDate", label: "Settlement Date" }, // SETTLEMENT DATE (Value Date)
  { key: "settlementAmount", label: "Settlement Amount" }, // SETTLEMENT AMOUNT (totalValue)
  { key: "quantity", label: "Quantity" }, // QTY (nominalValueTransferred)
  { key: "uniqueRefNum", label: "System Ref No" }, // UNIQUE_REF_NUM (transfer id)
  { key: "refNo", label: "Client Ref No" }, // REFNO (paymentReference)
  { key: "counterparty", label: "Counterparty Code" }, // COUNTERPARTY
  { key: "counterpartyName", label: "Counterparty Name" }, // COUNTERPARTY NAME
  { key: "cashAccount", label: "Cash Acc No" }, // CASH_ACCOUNT (counterparty bank acc number per latest spec)
  { key: "maker", label: "Inputter" }, // MAKER (l1ApprovedBy)
  { key: "makerDate", label: "Input Date" }, // MAKERDT (l1ApprovedAt)
  { key: "checker", label: "Authorizer" }, // CHECKER (l2ApprovedBy)
  { key: "checkerDate", label: "Authorize Date" }, // CHECKERDT (l2ApprovedAt) * spec said reuse l1ApprovedAt but we use proper l2 if exists
  { key: "nonBillable", label: "Billing Tag" }, // NON_BILLABLE (placeholder '-')
  { key: "scriptlessFlag", label: "Scripless Flag" }, // SCRIPTLESS FLAG (bond.scriplessFlag)
  { key: "tradeStatus", label: "Trade Status" }, // TRADESTATUS (transfer status)
  { key: "walletNo", label: "Wallet No" }, // WALLET_NO (sub-investor wallet address)
];

const TRADE_INSTRUCTION_COLUMNS = [
  { key: "tradeType", label: "Trade Type" },
  { key: "custodyAccountNo", label: "Custody Account No" },
  { key: "scanCode", label: "Scan Code" },
  { key: "stampDuty", label: "Stamp Duty" },
  { key: "isinCode", label: "ISIN Code" },
  { key: "clientRefNo", label: "Client Ref No" },
  { key: "issTag", label: "ISS Tag" },
  { key: "tradeDate", label: "Trade Date" },
  { key: "valueDate", label: "Value Date" },
  { key: "noOfShares", label: "No Of Shares" },
  { key: "settlementAmount", label: "Settlement Amount" },
  { key: "counterpartyAdaCode", label: "Counterparty ADA Code" },
  { key: "clearingFee", label: "Clearing Fee" },
  { key: "clearingFeeGst", label: "Clearing Fee GST" },
  { key: "buySellPrice", label: "Buy/Sell Price" },
  { key: "brokerageFee", label: "Brokerage Fee" },
  { key: "brokerageFeeGst", label: "Brokerage Fee GST" },
  { key: "ssaAccountNo", label: "SSA Account No" },
  { key: "accruedInterest", label: "Accrued Interest" },
  { key: "turnaroundFlag", label: "Turnaround Flag" },
  { key: "walletNo", label: "Wallet No" },
];

const INVESTOR_HOLDINGS_COLUMNS = [
  { key: "custodialAccountId", label: "Custodial Account" },
  { key: "investorName", label: "Investor Name" },
  { key: "investorType", label: "Investor Type" },
  { key: "holdingAmount", label: "Holding Amount" },
  { key: "holdingPercentage", label: "Holding %" },
  { key: "profitDistributed", label: "Profit Distributed" },
  { key: "custodialFee", label: "Custodial Fee" },
  { key: "paymentMethod", label: "Payment Method" },
  { key: "paymentStatus", label: "Payment Status" },
];

const INVESTOR_TYPE_COLUMNS = [
  { key: "type", label: "Investor Type" },
  { key: "count", label: "Count" },
  { key: "totalHolding", label: "Total Holding" },
  { key: "totalProfit", label: "Total Profit" },
];

const BOND_BREAKDOWN_COLUMNS = [
  { key: "bondName", label: "Bond Name" },
  { key: "bondType", label: "Type" },
  { key: "outstandingAmount", label: "Outstanding Amount" },
  { key: "investors", label: "Investors" },
  { key: "totalAccrued", label: "Total Accrued" },
  { key: "custodyFee", label: "Custody Fee" },
];

const TOP_INVESTOR_COLUMNS = [
  { key: "accountId", label: "Account ID" },
  { key: "investorName", label: "Investor Name" },
  { key: "totalHoldings", label: "Total Holdings" },
  { key: "accruedProfit", label: "Accrued Profit" },
  { key: "custodyFee", label: "Custody Fee" },
];

const SECTION_BUILDERS = [
  {
    key: "summary",
    build: (data) => {
      if (!data.summary || typeof data.summary !== "object") return null;
      const rows = Object.entries(data.summary).map(([key, value]) => ({
        metric: formatLabel(key),
        value: formatPrimitive(value),
      }));
      return {
        title: "Summary",
        columns: [
          { key: "metric", label: "Metric" },
          { key: "value", label: "Value" },
        ],
        rows,
      };
    },
  },
  {
    key: "custodialServices",
    build: (data) => {
      if (!data.custodialServices || typeof data.custodialServices !== "object")
        return null;
      const { servicesProvided, ...serviceMetrics } = data.custodialServices;
      const sections = [];
      if (Object.keys(serviceMetrics).length) {
        sections.push({
          title: "Custodial Services Summary",
          columns: [
            { key: "metric", label: "Metric" },
            { key: "value", label: "Value" },
          ],
          rows: Object.entries(serviceMetrics).map(([key, value]) => ({
            metric: formatLabel(key),
            value: formatPrimitive(value),
          })),
        });
      }
      if (Array.isArray(servicesProvided) && servicesProvided.length) {
        sections.push({
          title: "Custodial Service List",
          columns: [
            { key: "index", label: "#" },
            { key: "service", label: "Service" },
          ],
          rows: servicesProvided.map((service, index) => ({
            index: index + 1,
            service: formatPrimitive(service),
          })),
        });
      }
      return sections.length ? sections : null;
    },
  },
  {
    key: "bondBreakdown",
    build: (data) => {
      if (!Array.isArray(data.bondBreakdown) || !data.bondBreakdown.length)
        return null;
      return {
        title: "Bond Portfolio Breakdown",
        columns: BOND_BREAKDOWN_COLUMNS,
        rows: data.bondBreakdown,
      };
    },
  },
  {
    key: "topInvestorAccounts",
    build: (data) => {
      if (
        !Array.isArray(data.topInvestorAccounts) ||
        !data.topInvestorAccounts.length
      )
        return null;
      return {
        title: "Top Custodial Accounts",
        columns: TOP_INVESTOR_COLUMNS,
        rows: data.topInvestorAccounts,
      };
    },
  },
  {
    key: "investorHoldings",
    build: (data) => {
      if (
        !Array.isArray(data.investorHoldings) ||
        !data.investorHoldings.length
      )
        return null;
      return {
        title: "Custodial Holdings & Profit Distribution",
        columns: INVESTOR_HOLDINGS_COLUMNS,
        rows: data.investorHoldings,
      };
    },
  },
  {
    key: "investorTypeAnalysis",
    build: (data) => {
      if (
        !Array.isArray(data.investorTypeAnalysis) ||
        !data.investorTypeAnalysis.length
      )
        return null;
      return {
        title: "Investor Type Analysis",
        columns: INVESTOR_TYPE_COLUMNS,
        rows: data.investorTypeAnalysis,
      };
    },
  },
  {
    key: "positions",
    build: (data) => {
      if (!Array.isArray(data.positions) || !data.positions.length) return null;
      const rows = data.positions.map((position) => ({
        ...position,
        positionDate: data.positionDate || "",
        safekeepingPosition: position.safekeepingPosition,
      }));
      return {
        title: "Daily Position Details",
        columns: DAILY_POSITION_COLUMNS,
        rows,
      };
    },
  },
  {
    key: "transactions",
    build: (data) => {
      if (!Array.isArray(data.transactions) || !data.transactions.length)
        return null;
      return {
        title: "Transaction File Details",
        columns: TRANSACTION_FILE_COLUMNS,
        rows: data.transactions,
      };
    },
  },
  {
    key: "tradeInstructions",
    build: (data) => {
      if (
        !Array.isArray(data.tradeInstructions) ||
        !data.tradeInstructions.length
      )
        return null;
      return {
        title: "Trade Instruction File Details",
        columns: TRADE_INSTRUCTION_COLUMNS,
        rows: data.tradeInstructions,
      };
    },
  },
];

const buildReportSections = (data) => {
  if (!data) return [];
  return SECTION_BUILDERS.flatMap((builder) => {
    const result = builder.build(data);
    if (!result) return [];
    return Array.isArray(result) ? result : [result];
  });
};

const buildReportMetadata = (data, report) => {
  const metadata = [];
  const resolvedTitle = data?.reportTitle || report?.reportType || report?.name;
  if (resolvedTitle) metadata.push(["Report Title", resolvedTitle]);
  if (data?.reportPeriod) metadata.push(["Report Period", data.reportPeriod]);
  if (data?.bondName) metadata.push(["Bond", data.bondName]);
  if (data?.custodianBankName)
    metadata.push(["Custodian Bank", data.custodianBankName]);
  if (data?.reportGeneratedAt)
    metadata.push(["Generated At", data.reportGeneratedAt]);
  if (data?.positionDate) metadata.push(["Position Date", data.positionDate]);
  if (data?.summary?.distributionDate)
    metadata.push(["Distribution Date", data.summary.distributionDate]);
  return metadata;
};

const csvQuote = (value) => {
  const str = formatPrimitive(value);
  return `"${str.replace(/"/g, '""')}"`;
};

const makeFileName = (report, fallbackTitle, extension) => {
  const base = slugify(
    report?.reportType || report?.name || fallbackTitle || "report"
  );
  const timestamp = new Date().toISOString().split("T")[0];
  return `${base}-${timestamp}.${extension}`;
};

const chunkColumns = (columns, targetSize = 6) => {
  if (!Array.isArray(columns) || columns.length <= targetSize) {
    return [columns];
  }

  const chunks = [];
  for (let i = 0; i < columns.length; i += targetSize) {
    chunks.push(columns.slice(i, i + targetSize));
  }
  return chunks;
};

const exportToCSV = (report, data) => {
  const sections = buildReportSections(data);
  const metadata = buildReportMetadata(data, report);

  if (!sections.length && !metadata.length) {
    const blob = new Blob([JSON.stringify(data ?? {}, null, 2)], {
      type: "application/json;charset=utf-8;",
    });
    saveAs(blob, makeFileName(report, "report", "json"));
    return;
  }

  const lines = [];
  metadata.forEach(([label, value]) => {
    lines.push(`${csvQuote(label)},${csvQuote(value)}`);
  });
  if (metadata.length) {
    lines.push("");
  }

  sections.forEach((section, index) => {
    lines.push(csvQuote(section.title));
    lines.push(section.columns.map((col) => csvQuote(col.label)).join(","));
    section.rows.forEach((row) => {
      lines.push(
        section.columns.map((col) => csvQuote(row[col.key])).join(",")
      );
    });
    if (index !== sections.length - 1) {
      lines.push("");
    }
  });

  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  saveAs(blob, makeFileName(report, data?.reportTitle, "csv"));
};

const exportToPDF = (report, data) => {
  const sections = buildReportSections(data);
  const metadata = buildReportMetadata(data, report);

  if (!sections.length && !metadata.length) {
    const blob = new Blob([JSON.stringify(data ?? {}, null, 2)], {
      type: "application/json;charset=utf-8;",
    });
    saveAs(blob, makeFileName(report, "report", "json"));
    return;
  }

  const needsLandscape = sections.some((section) => section.columns.length > 6);
  const doc = new jsPDF({
    orientation: needsLandscape ? "landscape" : "portrait",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let cursorY = margin + 10;

  const title =
    data?.reportTitle || report?.reportType || report?.name || "Report";
  doc.setFontSize(16);
  doc.text(title, margin, cursorY);
  cursorY += 8;

  if (metadata.length) {
    if (cursorY > doc.internal.pageSize.getHeight() - margin - 20) {
      doc.addPage();
      cursorY = margin + 10;
    }

    autoTable(doc, {
      startY: cursorY,
      head: [["Field", "Value"]],
      body: metadata.map(([label, value]) => [label, formatPrimitive(value)]),
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
        halign: "left",
        valign: "middle",
      },
      headStyles: {
        fillColor: [226, 232, 240],
        textColor: [30, 41, 59],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: pageWidth - margin * 2 - 60 },
      },
      margin: { left: margin, right: margin },
    });

    cursorY = doc.lastAutoTable.finalY || cursorY;
  }

  sections.forEach((section) => {
    const columnGroups = chunkColumns(
      section.columns,
      section.columns.length > 10 ? 6 : 8
    );

    columnGroups.forEach((columnGroup, index) => {
      cursorY += 18;
      if (cursorY > doc.internal.pageSize.getHeight() - margin) {
        doc.addPage();
        cursorY = margin + 10;
      }

      const sectionTitle =
        columnGroups.length > 1
          ? `${section.title} (Part ${index + 1}/${columnGroups.length})`
          : section.title;

      doc.setFontSize(12);
      doc.text(sectionTitle, margin, cursorY);
      cursorY += 4;

      autoTable(doc, {
        startY: cursorY,
        head: [columnGroup.map((col) => col.label)],
        body: section.rows.map((row) =>
          columnGroup.map((col) => formatPrimitive(row[col.key]))
        ),
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          halign: "left",
          valign: "middle",
        },
        headStyles: {
          fillColor: [29, 78, 216],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: columnGroup.reduce((acc, _col, idx) => {
          acc[idx] = {
            minCellWidth: needsLandscape ? 28 : 35,
            overflow: "linebreak",
          };
          return acc;
        }, {}),
        margin: { left: margin, right: margin },
      });

      cursorY = doc.lastAutoTable.finalY || cursorY;
    });
  });

  doc.save(makeFileName(report, data?.reportTitle, "pdf"));
};

const CustodianReporting = () => {
  const [selectedReport, setSelectedReport] = useState("");
  const [dateRange, setDateRange] = useState("last30days");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [filterInvestor, setFilterInvestor] = useState("all");
  const [filterBond, setFilterBond] = useState("all");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [activeTab, setActiveTab] = useState("generate");
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState(null);
  const [selectedReportMeta, setSelectedReportMeta] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [reportSummary, setReportSummary] = useState(null);

  // Mock data for report types
  const reportTypes = [
    {
      id: "daily_profit_accrual",
      name: "Daily Profit Accrual Report",
      description:
        "Daily accrual calculations and profit distributions for custodial accounts",
      icon: Calendar,
      estimatedTime: "2-4 minutes",
    },
    {
      id: "investor_distribution",
      name: "Investor Distribution Report",
      description:
        "Sukuk profit distribution details and custodial investor analysis",
      icon: TrendingUp,
      estimatedTime: "2-3 minutes",
    },
  ];

  // Mock data for investors and bonds
  const investors = [
    { id: "INV-001", name: "Ahmad bin Rahman", type: "Individual" },
    { id: "INV-002", name: "Tech Solutions Sdn Bhd", type: "Corporate" },
    { id: "INV-003", name: "Jennifer Wong Su Ling", type: "Individual" },
    { id: "INV-004", name: "Global Investment Ltd", type: "Corporate" },
  ];

  const bonds = [
    "Green Infrastructure Bond 2024-A",
    "Tech Development Sukuk",
    "Infrastructure Bond 2022-A",
    "Corporate Expansion Bond",
    "Renewable Energy Sukuk Series B",
  ];

  const getDefaultDailyPositionReport = () => ({
    reportTitle: "Daily Position Report",
    reportPeriod: new Date().toISOString().split("T")[0],
    positionDate: new Date().toLocaleDateString("en-GB"),
    positions: [],
    summary: {
      totalPositions: 0,
      totalSafekeepingPosition: 0,
      totalConventionalBonds: 0,
      totalSukuk: 0,
      uniqueAccounts: 0,
      uniqueSecurities: 0,
    },
  });

  const fetchReportData = async (report) => {
    if (!report) return null;
    const reportName = (report.reportType || report.name || "").toLowerCase();

    if (reportName.includes("daily profit accrual")) {
      return mockCustodianDailyProfitAccrualData;
    }

    if (reportName.includes("investor distribution")) {
      return mockCustodianInvestorDistributionData;
    }

    if (reportName.includes("daily position")) {
      try {
        const resp = await custodianAPI.getDailyPositionReport();
        const data = resp?.data?.data || resp?.data;
        if (data?.positions) {
          return data;
        }
        return getDefaultDailyPositionReport();
      } catch (error) {
        console.error("Failed to load daily position report:", error);
        return {
          ...getDefaultDailyPositionReport(),
          reportTitle: "Daily Position Report (Error)",
          error: "Unable to fetch report data.",
        };
      }
    }

    if (reportName.includes("transaction file")) {
      // Live Transaction File data generation using secondary transfers
      try {
        const todayISO = new Date().toISOString().split("T")[0];
        const computeStart = () => {
          const today = new Date();
          switch (dateRange) {
            case "last7days":
              return new Date(today.setDate(today.getDate() - 7))
                .toISOString()
                .split("T")[0];
            case "last30days":
              return new Date(today.setDate(today.getDate() - 30))
                .toISOString()
                .split("T")[0];
            case "last90days":
              return new Date(today.setDate(today.getDate() - 90))
                .toISOString()
                .split("T")[0];
            case "custom":
              return customStartDate || todayISO;
            default:
              return todayISO;
          }
        };
        const startDate =
          dateRange === "custom" ? customStartDate || todayISO : computeStart();
        const endDate =
          dateRange === "custom" ? customEndDate || todayISO : todayISO;

        // Paginated fetch of secondary transfers within date range
        const aggregatedTransfers = [];
        const perPageLimit = 100;
        let page = 1;
        const maxPages = 20;
        let fetched = 0;
        let total = Infinity;
        while (fetched < total && page <= maxPages) {
          const resp = await secondaryTransfersAPI.getTransfers({
            fromDate: startDate,
            toDate: endDate,
            limit: perPageLimit,
            page,
            sortBy: "createdAt",
            sortOrder: "desc",
          });
          const raw = resp?.data?.data || resp?.data;
          const transfers = Array.isArray(raw?.transfers)
            ? raw.transfers
            : Array.isArray(raw)
            ? raw
            : [];
          const pagination = raw?.pagination;
          aggregatedTransfers.push(...transfers);
          fetched = aggregatedTransfers.length;
          total = pagination?.total ?? fetched;
          if (
            !pagination ||
            fetched >= total ||
            transfers.length < perPageLimit
          )
            break;
          page += 1;
        }

        // Normalize ID
        const normId = (val) => {
          if (!val) return "";
          if (typeof val === "string") return val;
          if (typeof val === "object") return val._id || "";
          return "";
        };

        // Fetch sub-investors list
        let subInvestorMap = new Map(); // id -> {name, bankAccounts?}
        try {
          const siResp = await custodianAPI.getSubInvestors();
          const subInvestors =
            siResp?.data?.subInvestors ||
            siResp?.data?.data ||
            siResp?.data ||
            [];
          if (Array.isArray(subInvestors)) {
            subInvestors.forEach((si) => {
              const sid = si?._id || si?.id || si?.userId;
              if (sid) subInvestorMap.set(String(sid), si);
            });
          }
        } catch (e) {
          console.warn("Unable to load sub-investor list:", e?.message || e);
        }

        const subInvestorIds = new Set([...subInvestorMap.keys()]);

        // Filter transfers where either client or counterparty is a sub-investor
        const relevantTransfers = aggregatedTransfers.filter((t) => {
          const clientIdStr = normId(t.clientId);
          const counterpartyIdStr = normId(t.counterpartyId);
          return (
            (clientIdStr && subInvestorIds.has(clientIdStr)) ||
            (counterpartyIdStr && subInvestorIds.has(counterpartyIdStr))
          );
        });

        // Build allocation-driven wallet mapping (bond + investor specific)
        // 1. Collect unique (bondId, subInvestorId) pairs from relevant transfers
        const bondInvestorPairs = new Set();
        relevantTransfers.forEach((t) => {
          const bondId = normId(t.bondId);
          if (!bondId) return;
          const clientIdStr = normId(t.clientId);
          const counterpartyIdStr = normId(t.counterpartyId);
          if (clientIdStr && subInvestorIds.has(clientIdStr)) {
            bondInvestorPairs.add(`${bondId}::${clientIdStr}`);
          }
          if (counterpartyIdStr && subInvestorIds.has(counterpartyIdStr)) {
            bondInvestorPairs.add(`${bondId}::${counterpartyIdStr}`);
          }
        });

        // 2. Fetch custodian allocations (preview view) in bulk (large limit) then map
        let allocationList = [];
        try {
          const allocResp = await custodianAPI.getAllCustodianAllocations({
            limit: 1000,
          });
          const rawAlloc =
            allocResp?.data?.allocations ||
            allocResp?.data?.data ||
            allocResp?.data ||
            [];
          allocationList = Array.isArray(rawAlloc) ? rawAlloc : [];
        } catch (e) {
          console.warn(
            "Unable to load custodian allocations for wallet resolution:",
            e?.message || e
          );
        }

        // Map: key = bondId::investorId -> { walletId, walletAddress (string or doc), allocation }
        const allocationWalletMap = new Map();
        allocationList.forEach((a) => {
          const bondId = normId(a.bondId);
          const investorId = a.investorId || normId(a.investorId);
          if (!bondId || !investorId) return;
          const key = `${bondId}::${investorId}`;
          if (!allocationWalletMap.has(key)) {
            const walletDoc = a.walletAddress; // populated doc or id
            const walletId =
              typeof walletDoc === "object" ? walletDoc?._id || "" : walletDoc;
            const walletAddrString =
              typeof walletDoc === "object"
                ? walletDoc?.walletAddress || ""
                : "";
            allocationWalletMap.set(key, {
              walletId,
              allocation: a,
              walletAddress: walletAddrString,
            });
          }
        });

        // 3. For each sub-investor involved (from relevantTransfers) fetch all wallets ONCE and keep full list
        const involvedSubInvestorIds = new Set();
        relevantTransfers.forEach((t) => {
          const cId = normId(t.clientId);
          const cpId = normId(t.counterpartyId);
          if (cId && subInvestorIds.has(cId)) involvedSubInvestorIds.add(cId);
          if (cpId && subInvestorIds.has(cpId))
            involvedSubInvestorIds.add(cpId);
        });

        const subInvestorWalletsMap = new Map(); // investorId -> array of wallets
        await Promise.all(
          [...involvedSubInvestorIds].map(async (sid) => {
            try {
              const wResp = await custodianAPI.getSubInvestorWallets(sid);
              const wallets = wResp?.data?.wallets || [];
              subInvestorWalletsMap.set(sid, wallets);
            } catch (e) {
              subInvestorWalletsMap.set(sid, []);
            }
          })
        );

        // Helper to resolve wallet for (investorId, bondId): prefer allocation-specific wallet, else first wallet
        const resolveWallet = (investorId, bondId) => {
          if (!investorId) return { walletAddress: "", accountNumber: "" };
          const key = `${bondId}::${investorId}`;
          const allocEntry = allocationWalletMap.get(key);
          const wallets = subInvestorWalletsMap.get(investorId) || [];
          if (allocEntry && allocEntry.walletId) {
            const matched = wallets.find(
              (w) =>
                w._id === allocEntry.walletId || w.id === allocEntry.walletId
            );
            if (matched) {
              return {
                walletAddress:
                  matched.walletAddress || allocEntry.walletAddress || "",
                accountNumber: matched.bankAccountDetails?.accountNumber || "",
              };
            }
            // Allocation had wallet doc with address but we lack accountNumber
            return {
              walletAddress: allocEntry.walletAddress || "",
              accountNumber: "",
            };
          }
          // No allocation-specific wallet found; fallback to first available wallet
          if (wallets.length) {
            const w = wallets[0];
            return {
              walletAddress: w.walletAddress || "",
              accountNumber: w.bankAccountDetails?.accountNumber || "",
            };
          }
          return { walletAddress: "", accountNumber: "" };
        };

        // Helper date format dd/mm/yyyy
        const fmt = (d) => {
          if (!d) return "";
          const dateObj = new Date(d);
          if (isNaN(dateObj.getTime())) return "";
          const dd = String(dateObj.getDate()).padStart(2, "0");
          const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
          const yyyy = dateObj.getFullYear();
          return `${dd}/${mm}/${yyyy}`;
        };

        // Master (custodian) details – assume single current custodian derived from first sub-investor's createdBy or reuse fields inside subInvestor objects
        // Fallback placeholders until dedicated endpoint for custodian profile is integrated.
        // Attempt to infer custodian id/name from any subInvestor.createdBy / createdByName
        let masterAccountNo = "";
        let masterAccountName = "";
        // New: try fetching custodian wallets to obtain a definitive custodian bank account number
        try {
          // Fetch custodian profile for proper name
          const profileResp = await authAPI.getProfile();
          const custodianName =
            profileResp?.data?.corporateInfo?.companyName ||
            profileResp?.data?.corporateInfo?.legalEntityName ||
            profileResp?.data?.email;
          if (custodianName) masterAccountName = custodianName;

          // Fetch wallets to obtain account number
          const cwResp = await custodianAPI.getCustodianWallets();
          const custodianWallets = cwResp?.data?.wallets || cwResp?.data || [];
          if (Array.isArray(custodianWallets) && custodianWallets.length) {
            const preferred =
              custodianWallets.find(
                (w) => (w.accountType || "").toLowerCase() === "conventional"
              ) || custodianWallets[0];
            masterAccountNo = preferred?.accountNumber || masterAccountNo;
          }
        } catch (e) {
          // Silent fallback
        }
        // Fallback: derive from any sub-investor's creator if still missing
        if (!masterAccountNo || !masterAccountName) {
          for (const si of subInvestorMap.values()) {
            if (si?.createdBy) {
              const creator = si.createdBy;
              masterAccountNo =
                masterAccountNo ||
                creator?.bankAccountDetails?.accountNumber ||
                creator?.custodianBankAccountNumber ||
                creator._id ||
                creator.id ||
                creator;
              masterAccountName =
                masterAccountName ||
                creator?.corporateInfo?.companyName ||
                creator?.displayName ||
                creator?.email;
              if (masterAccountNo && masterAccountName) break;
            }
          }
        }
        // Final safety: mask obvious object dumps
        if (typeof masterAccountNo === "object") {
          masterAccountNo = masterAccountNo?._id || masterAccountNo?.id || "";
        }

        const transactions = relevantTransfers.map((t) => {
          const clientIdStr = normId(t.clientId);
          const counterpartyIdStr = normId(t.counterpartyId);
          const bond = t.bondId || {}; // may be populated
          const bondIsin = bond.isin || t.stockCode || "";
          const bondName = bond.bondName || bond.name || "";
          const bondLocation = bond.locationCode || "";
          const bondCurrency = bond.currency || t.currency || "";

          // Determine which side is the sub-investor taking the "ACCOUNT" fields: prefer client else counterparty
          const isClientSub = clientIdStr && subInvestorIds.has(clientIdStr);
          const accountId = isClientSub
            ? clientIdStr
            : subInvestorIds.has(counterpartyIdStr)
            ? counterpartyIdStr
            : "";
          const accountInfo = subInvestorMap.get(accountId) || {};
          const accountName =
            accountInfo?.corporateInfo?.companyName ||
            accountInfo?.displayName ||
            accountInfo?.fullName ||
            accountInfo?.name ||
            "";

          // Counterparty fields (the opposite party if sub-investor is client else use client)
          const counterpartyId = isClientSub ? counterpartyIdStr : clientIdStr;
          let counterpartyName = "";
          if (isClientSub) {
            counterpartyName =
              t.counterpartyName ||
              t.counterpartyId?.corporateInfo?.companyName ||
              t.counterpartyId?.displayName ||
              t.counterpartyId?.fullName ||
              "";
          } else {
            counterpartyName =
              t.clientName ||
              t.clientId?.corporateInfo?.companyName ||
              t.clientId?.displayName ||
              t.clientId?.fullName ||
              "";
          }

          // Resolve wallet for custody (sub-investor) side (still used for walletNo & custodyAccountNo)
          const resolvedWallet = resolveWallet(accountId, normId(bond));

          // Cash Account per updated requirement: counterparty bank account number (NOT the custody/sub-investor wallet)
          const resolveBankAcc = (partyObj) => {
            if (!partyObj || typeof partyObj !== "object") return undefined;
            return (
              partyObj.bankAccountDetails?.accountNumber ||
              partyObj.bankAccountDetails?.iban ||
              partyObj.bankAccountNumber ||
              partyObj.bankAccountNo ||
              partyObj.accountNumber ||
              partyObj.iban ||
              undefined
            );
          };
          const counterpartyObj = isClientSub ? t.counterpartyId : t.clientId;
          // Prefer enriched counterpartyWallet (attached by backend) first
          let counterpartyBankAccountNo =
            t.counterpartyWallet?.accountNumber ||
            resolveBankAcc(counterpartyObj) ||
            t.counterpartyBankAccountNumber ||
            t.counterpartyBankAccountNo ||
            t.counterpartyAccountNumber ||
            t.counterpartyBankAccNo ||
            t.counterpartyBankAccount?.accountNumber ||
            t.counterpartyBankAccount ||
            "";
          // Fallback: search bankAccounts array on populated counterparty user
          if (
            !counterpartyBankAccountNo &&
            counterpartyObj &&
            Array.isArray(counterpartyObj.bankAccounts) &&
            counterpartyObj.bankAccounts.length
          ) {
            const preferredAcc =
              counterpartyObj.bankAccounts.find(
                (acc) => acc.accountType === "conventional"
              ) || counterpartyObj.bankAccounts[0];
            counterpartyBankAccountNo =
              preferredAcc?.accountNumber || counterpartyBankAccountNo;
          }
          if (!counterpartyBankAccountNo) counterpartyBankAccountNo = "-";
          const cashAccount = counterpartyBankAccountNo;

          // Approvals (maker/checker)
          const maker =
            t.l1ApprovedBy?.displayName ||
            t.l1ApprovedBy?.email ||
            (typeof t.l1ApprovedBy === "string" ? t.l1ApprovedBy : "") ||
            "";
          const makerDate = fmt(t.l1ApprovedAt);
          const checker =
            t.l2ApprovedBy?.displayName ||
            t.l2ApprovedBy?.email ||
            (typeof t.l2ApprovedBy === "string" ? t.l2ApprovedBy : "") ||
            "";
          const checkerDate = fmt(t.l2ApprovedAt || t.l1ApprovedAt); // fallback per spec to L1 if L2 missing

          return {
            masterAccountNo: masterAccountNo || "-",
            masterAccountName: masterAccountName || "-",
            // custodyAccountNo should represent the sub-investor's bank account number per new requirement
            custodyAccountNo: resolvedWallet.accountNumber || accountId || "-",
            custodyAccountName: accountName || "-",
            securityCode: bondIsin || "-",
            securityName: bondName || "-",
            securityLocation: bondLocation || "-",
            currency: bondCurrency || "-",
            tradeDate: fmt(t.tradeDate),
            settlementDate: fmt(t.valueDate),
            settlementAmount: t.totalValue != null ? t.totalValue : "",
            quantity:
              t.nominalValueTransferred != null
                ? t.nominalValueTransferred
                : "",
            uniqueRefNum: t._id || "-",
            refNo: t.paymentReference || "-",
            counterparty: counterpartyId || "-",
            counterpartyName: counterpartyName || "-",
            cashAccount,
            maker: maker || "-",
            makerDate: makerDate || "-",
            checker: checker || "-",
            checkerDate: checkerDate || "-",
            nonBillable: "-",
            scriptlessFlag: bond.scriplessFlag ? "Y" : "N",
            tradeStatus: t.status || "-",
            walletNo: resolvedWallet.walletAddress || "",
          };
        });

        const summary = {
          totalTransactions: transactions.length,
          totalSettlementAmount: transactions.reduce(
            (acc, tx) => acc + (Number(tx.settlementAmount) || 0),
            0
          ),
          uniqueCounterparties: new Set(
            transactions.map((tx) => tx.counterparty).filter(Boolean)
          ).size,
          uniqueSecurities: new Set(
            transactions.map((tx) => tx.securityCode).filter(Boolean)
          ).size,
        };

        const reportPeriod = `${fmt(startDate)} to ${fmt(endDate)}`;
        return {
          reportTitle: "Transaction File Report",
          reportPeriod,
          transactions,
          summary,
        };
      } catch (error) {
        console.error("Failed to load transaction file data:", error);
        return {
          reportTitle: "Transaction File Report (Error)",
          reportPeriod: "",
          transactions: [],
          summary: {},
          error: "Unable to fetch transaction file data.",
        };
      }
    }

    if (reportName.includes("trade instruction")) {
      // Real data implementation with pagination respecting backend limit (<=100)
      try {
        const todayISO = new Date().toISOString().split("T")[0];
        const computeStart = () => {
          const today = new Date();
          switch (dateRange) {
            case "last7days":
              return new Date(today.setDate(today.getDate() - 7))
                .toISOString()
                .split("T")[0];
            case "last30days":
              return new Date(today.setDate(today.getDate() - 30))
                .toISOString()
                .split("T")[0];
            case "last90days":
              return new Date(today.setDate(today.getDate() - 90))
                .toISOString()
                .split("T")[0];
            case "custom":
              return customStartDate || todayISO;
            default:
              return todayISO;
          }
        };
        const startDate =
          dateRange === "custom" ? customStartDate || todayISO : computeStart();
        const endDate =
          dateRange === "custom" ? customEndDate || todayISO : todayISO;

        const aggregatedTransfers = [];
        const perPageLimit = 100; // backend enforced maximum
        let page = 1;
        const maxPages = 20; // safety cap (2000 records)
        let fetched = 0;
        let total = Infinity;

        while (fetched < total && page <= maxPages) {
          const resp = await secondaryTransfersAPI.getTransfers({
            fromDate: startDate,
            toDate: endDate,
            limit: perPageLimit,
            page,
            // Sort by creation time so the newest transfer records (latest ingested) appear first
            sortBy: "createdAt",
            // Request descending so newest transfers are returned first per page
            sortOrder: "desc",
          });
          const raw = resp?.data?.data || resp?.data;
          const transfers = Array.isArray(raw?.transfers)
            ? raw.transfers
            : Array.isArray(raw)
            ? raw
            : [];
          const pagination = raw?.pagination;
          aggregatedTransfers.push(...transfers);
          fetched = aggregatedTransfers.length;
          total = pagination?.total ?? fetched; // if no pagination fallback
          if (
            !pagination ||
            fetched >= total ||
            transfers.length < perPageLimit
          )
            break;
          page += 1;
        }

        // Ensure newest first even if backend ignores sortOrder: explicit client-side sort by createdAt desc (fallback to tradeDate)
        aggregatedTransfers.sort((a, b) => {
          const ad = new Date(a.createdAt || a.tradeDate).getTime();
          const bd = new Date(b.createdAt || b.tradeDate).getTime();
          return isNaN(bd) - isNaN(ad) || bd - ad; // newest (largest timestamp) first
        });

        // Helper to normalize populated refs to their _id string
        const normId = (val) => {
          if (!val) return "";
          if (typeof val === "string") return val;
          if (typeof val === "object") return val._id || "";
          return "";
        };

        // Fetch sub-investors under this custodian to build an exclusion list
        let subInvestorIds = new Set();
        try {
          const siResp = await custodianAPI.getSubInvestors();
          const subInvestors =
            siResp?.data?.subInvestors ||
            siResp?.data?.data ||
            siResp?.data ||
            [];
          if (Array.isArray(subInvestors)) {
            subInvestors.forEach((si) => {
              const sid = si?._id || si?.id || si?.userId;
              if (sid) subInvestorIds.add(sid.toString());
            });
          }
        } catch (e) {
          console.warn(
            "Unable to load sub-investor list for exclusion filter:",
            e?.message || e
          );
        }

        // Requirement: ONLY show transfers where a sub-investor participates (as client or counterparty)
        const clientTransfers = aggregatedTransfers.filter((t) => {
          const clientIdStr = normId(t.clientId);
          const counterpartyIdStr = normId(t.counterpartyId);
          return (
            (clientIdStr && subInvestorIds.has(clientIdStr)) ||
            (counterpartyIdStr && subInvestorIds.has(counterpartyIdStr))
          );
        });

        // Collect all involved sub-investor IDs for wallet lookup (client or counterparty role)
        const involvedSubInvestorIds = new Set();
        clientTransfers.forEach((t) => {
          const cId = normId(t.clientId);
          const cpId = normId(t.counterpartyId);
          if (cId && subInvestorIds.has(cId)) involvedSubInvestorIds.add(cId);
          if (cpId && subInvestorIds.has(cpId))
            involvedSubInvestorIds.add(cpId);
        });

        // Wallet lookups for all involved sub-investor IDs (capture wallet address & bank account number)
        const walletMap = {};
        await Promise.all(
          [...involvedSubInvestorIds].map(async (cid) => {
            try {
              const wResp = await custodianAPI.getSubInvestorWallets(cid);
              const wallets = wResp?.data?.wallets || [];
              if (wallets.length) {
                const w = wallets[0];
                walletMap[cid] = {
                  walletAddress: w.walletAddress || "",
                  accountNumber: w.bankAccountDetails?.accountNumber || "",
                };
              } else {
                walletMap[cid] = { walletAddress: "", accountNumber: "" };
              }
            } catch (e) {
              walletMap[cid] = { walletAddress: "", accountNumber: "" };
            }
          })
        );

        const fmt = (d) => {
          if (!d) return "";
          const dateObj = new Date(d);
          if (isNaN(dateObj.getTime())) return "";
          const dd = String(dateObj.getDate()).padStart(2, "0");
          const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
          const yyyy = dateObj.getFullYear();
          return `${dd}/${mm}/${yyyy}`;
        };

        const tradeInstructions = clientTransfers.map((t) => {
          const clientIdStr = normId(t.clientId);
          const bondIdStr = normId(t.bondId);
          const counterpartyIdStr = normId(t.counterpartyId);
          // Decide which party is the sub-investor for custodyAccountNo (prefer client, else counterparty)
          const subInvestorAccountId = subInvestorIds.has(clientIdStr)
            ? clientIdStr
            : subInvestorIds.has(counterpartyIdStr)
            ? counterpartyIdStr
            : "";
          return {
            tradeType: t.transferType,
            // custodyAccountNo must show the Sub-Investor BANK ACCOUNT NUMBER (not the internal ID)
            // We retrieve this from walletMap (populated earlier with bankAccountDetails.accountNumber)
            // Fallback: if no bank account number is available, leave blank (spec requirement prefers bank account only)
            custodyAccountNo: subInvestorAccountId
              ? walletMap[subInvestorAccountId]?.accountNumber || ""
              : "",
            scanCode: bondIdStr,
            stampDuty: "-",
            isinCode: t.stockCode || t.bondId?.stockCode || "",
            clientRefNo: t.paymentReference || "",
            issTag: "-",
            tradeDate: fmt(t.tradeDate),
            valueDate: fmt(t.valueDate),
            noOfShares:
              t.nominalValueTransferred != null
                ? t.nominalValueTransferred
                : "",
            settlementAmount: t.totalValue != null ? t.totalValue : "",
            counterpartyAdaCode: "-", // placeholder
            clearingFee: "-",
            clearingFeeGst: "-",
            buySellPrice: "-",
            brokerageFee: "-",
            brokerageFeeGst: "-",
            ssaAccountNo: "-",
            accruedInterest: "-",
            turnaroundFlag: "-",
            walletNo: subInvestorAccountId
              ? walletMap[subInvestorAccountId]?.walletAddress || ""
              : "",
          };
        });

        const summary = {
          totalInstructions: tradeInstructions.length,
          buyOrders: tradeInstructions.filter((i) => i.tradeType === "Buy")
            .length,
          sellOrders: tradeInstructions.filter((i) => i.tradeType === "Sell")
            .length,
          totalTradedValue: tradeInstructions.reduce(
            (acc, i) => acc + (Number(i.settlementAmount) || 0),
            0
          ),
          totalClearingFees: 0,
          totalBrokerageFees: 0,
          uniqueCounterparties: new Set(
            clientTransfers.map((t) => normId(t.counterpartyId)).filter(Boolean)
          ).size,
        };

        // Always present an explicit date range for clarity (formatted dd/mm/yyyy)
        const reportPeriod = `${fmt(startDate)} to ${fmt(endDate)}`;
        return {
          reportTitle: "Trade Instruction File Report",
          reportPeriod,
          tradeInstructions,
          summary,
        };
      } catch (error) {
        console.error("Failed to load trade instruction file data:", error);
        return {
          reportTitle: "Trade Instruction File Report (Error)",
          reportPeriod: "",
          tradeInstructions: [],
          summary: {},
          error: "Unable to fetch trade instruction data.",
        };
      }
    }

    return null;
  };

  // Mock data for generated reports
  const [reportsHistory] = useState([
    {
      id: "RPT-001",
      reportType: "Daily Profit Accrual Report",
      generatedDate: "2024-01-22T09:15:00Z",
      dateRange: "January 2024",
      parameters: {
        startDate: "2024-01-01",
        endDate: "2024-01-22",
        investor: "All Investors",
        bond: "All Sukuk",
      },
      status: "completed",
      downloadUrl: "#",
      generatedBy: "custodian.admin@bank.com",
      fileSize: "5.2 MB",
    },
    {
      id: "RPT-002",
      reportType: "Verification Status Report",
      generatedDate: "2024-01-15T14:30:00Z",
      dateRange: "Last 30 Days",
      parameters: {
        startDate: "2023-12-16",
        endDate: "2024-01-15",
        investor: "All Investors",
        bond: "All Bonds",
      },
      status: "completed",
      downloadUrl: "#",
      generatedBy: "custodian.admin@bank.com",
      fileSize: "2.3 MB",
    },
    {
      id: "RPT-003",
      reportType: "Transaction History Report",
      generatedDate: "2024-01-14T11:45:00Z",
      dateRange: "Custom Range",
      parameters: {
        startDate: "2024-01-01",
        endDate: "2024-01-14",
        investor: "All Investors",
        bond: "Green Infrastructure Bond 2024-A",
      },
      status: "completed",
      downloadUrl: "#",
      generatedBy: "custodian.admin@bank.com",
      fileSize: "4.1 MB",
    },
    {
      id: "RPT-004",
      reportType: "Wallet Holdings Report",
      generatedDate: "2024-01-13T16:20:00Z",
      dateRange: "Last 7 Days",
      parameters: {
        startDate: "2024-01-06",
        endDate: "2024-01-13",
        investor: "All Investors",
        bond: "All Bonds",
      },
      status: "completed",
      downloadUrl: "#",
      generatedBy: "custodian.admin@bank.com",
      fileSize: "1.8 MB",
    },
    {
      id: "RPT-005",
      reportType: "Investor Distribution Report",
      generatedDate: "2024-01-25T10:30:00Z",
      dateRange: "January 2025",
      parameters: {
        startDate: "2025-01-01",
        endDate: "2025-01-31",
        investor: "All Investors",
        bond: "Green Sukuk Infrastructure 2027",
      },
      status: "completed",
      downloadUrl: "#",
      generatedBy: "custodian.admin@bank.com",
      fileSize: "3.2 MB",
    },
    {
      id: "RPT-006",
      reportType: "Daily Position Report",
      generatedDate: "2025-08-26T16:30:00Z",
      dateRange: "August 26, 2025",
      parameters: {
        startDate: "2025-08-26",
        endDate: "2025-08-26",
        investor: "All Investors",
        bond: "All Securities",
      },
      status: "completed",
      downloadUrl: "#",
      generatedBy: "custodian.admin@bank.com",
      fileSize: "1.5 MB",
    },
    {
      id: "RPT-007",
      reportType: "Transaction File",
      generatedDate: "2025-08-27T10:15:00Z",
      dateRange: "August 25-27, 2025",
      parameters: {
        startDate: "2025-08-25",
        endDate: "2025-08-27",
        investor: "All Investors",
        bond: "All Securities",
      },
      status: "completed",
      downloadUrl: "#",
      generatedBy: "custodian.admin@bank.com",
      fileSize: "2.8 MB",
    },
    {
      id: "RPT-008",
      reportType: "Trade Instruction File",
      generatedDate: "2025-08-08T14:30:00Z",
      dateRange: "August 5-8, 2025",
      parameters: {
        startDate: "2025-08-05",
        endDate: "2025-08-08",
        investor: "All Investors",
        bond: "All Securities",
      },
      status: "completed",
      downloadUrl: "#",
      generatedBy: "custodian.admin@bank.com",
      fileSize: "1.9 MB",
    },
  ]);

  // Mock Custodian Daily Profit Accrual Report Data
  const mockCustodianDailyProfitAccrualData = {
    reportTitle: "Custodian Daily Profit Accrual Report - January 2024",
    reportPeriod: "January 1, 2024 - January 22, 2024",
    totalCustodialAssets: 275000000,
    totalAccruedProfit: 1187500,
    totalInvestorAccounts: 342,
    summary: {
      totalSukukHoldings: 175000000,
      totalConventionalBonds: 100000000,
      averageDailyAccrual: 53977.27,
      totalDaysInPeriod: 22,
      custodyFeeEarned: 23750,
    },
    bondBreakdown: [
      {
        bondName: "Green Sukuk Infrastructure 2027",
        bondType: "Sukuk",
        outstandingAmount: 100000000,
        investors: 156,
        totalAccrued: 458750,
        custodyFee: 9175,
      },
      {
        bondName: "Tech Development Sukuk",
        bondType: "Sukuk",
        outstandingAmount: 75000000,
        investors: 89,
        totalAccrued: 343125,
        custodyFee: 6863,
      },
      {
        bondName: "Conventional Corporate Bond 2025",
        bondType: "Conventional",
        outstandingAmount: 100000000,
        investors: 97,
        totalAccrued: 385625,
        custodyFee: 7713,
      },
    ],
    topInvestorAccounts: [
      {
        accountId: "CUST-001",
        investorName: "Khazanah Nasional Berhad",
        totalHoldings: 45000000,
        accruedProfit: 206250,
        custodyFee: 4125,
      },
      {
        accountId: "CUST-002",
        investorName: "EPF Investment Scheme",
        totalHoldings: 35000000,
        accruedProfit: 160625,
        custodyFee: 3213,
      },
      {
        accountId: "CUST-003",
        investorName: "KWAP Fund",
        totalHoldings: 30000000,
        accruedProfit: 137500,
        custodyFee: 2750,
      },
      {
        accountId: "CUST-004",
        investorName: "Amanah Raya Berhad",
        totalHoldings: 25000000,
        accruedProfit: 114583,
        custodyFee: 2292,
      },
    ],
  };

  // Mock Custodian Investor Distribution Report Data
  const mockCustodianInvestorDistributionData = {
    reportTitle: "Custodian Investor Distribution Report - January 2025",
    bondName: "Green Sukuk Infrastructure 2027",
    reportPeriod: "January 1, 2025 - January 31, 2025",
    custodianBankName: "Global Custodian Bank",
    summary: {
      totalInvestors: 156,
      totalProfitDistributed: 2458750,
      averageProfitPerInvestor: 15761.86,
      distributionDate: "2025-02-01",
      paymentStatus: "Completed",
      custodialFeeEarned: 49175,
      totalAssetsUnderCustody: 100000000,
    },
    investorHoldings: [
      {
        custodialAccountId: "CUST-001",
        investorId: "INV-001",
        investorName: "Khazanah Nasional Berhad",
        investorType: "Government Linked Company",
        holdingAmount: 25000000,
        holdingPercentage: 25.0,
        profitDistributed: 614687.5,
        custodialFee: 12293.75,
        paymentMethod: "SWIFT Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-06-15",
      },
      {
        custodialAccountId: "CUST-002",
        investorId: "INV-002",
        investorName: "EPF Investment Scheme",
        investorType: "Retirement Fund",
        holdingAmount: 20000000,
        holdingPercentage: 20.0,
        profitDistributed: 491750.0,
        custodialFee: 9835.0,
        paymentMethod: "Domestic Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-06-18",
      },
      {
        custodialAccountId: "CUST-003",
        investorId: "INV-003",
        investorName: "KWAP Fund",
        investorType: "Government Pension Fund",
        holdingAmount: 15000000,
        holdingPercentage: 15.0,
        profitDistributed: 368812.5,
        custodialFee: 7376.25,
        paymentMethod: "Real-time Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-06-20",
      },
      {
        custodialAccountId: "CUST-004",
        investorId: "INV-004",
        investorName: "Amanah Raya Berhad",
        investorType: "Trust Company",
        holdingAmount: 12000000,
        holdingPercentage: 12.0,
        profitDistributed: 295050.0,
        custodialFee: 5901.0,
        paymentMethod: "SWIFT Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-06-25",
      },
      {
        custodialAccountId: "CUST-005",
        investorId: "INV-005",
        investorName: "Tabung Haji",
        investorType: "Islamic Institution",
        holdingAmount: 10000000,
        holdingPercentage: 10.0,
        profitDistributed: 245875.0,
        custodialFee: 4917.5,
        paymentMethod: "Islamic Banking Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-07-01",
      },
      {
        custodialAccountId: "CUST-006",
        investorId: "INV-006",
        investorName: "LTAT Fund",
        investorType: "Armed Forces Fund",
        holdingAmount: 8000000,
        holdingPercentage: 8.0,
        profitDistributed: 196700.0,
        custodialFee: 3934.0,
        paymentMethod: "Domestic Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-07-05",
      },
      {
        custodialAccountId: "CUST-007",
        investorId: "INV-007",
        investorName: "PNB Investment",
        investorType: "State Investment Arm",
        holdingAmount: 5000000,
        holdingPercentage: 5.0,
        profitDistributed: 122937.5,
        custodialFee: 2458.75,
        paymentMethod: "Real-time Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-07-10",
      },
      {
        custodialAccountId: "CUST-008",
        investorId: "INV-008",
        investorName: "SOCSO Investment",
        investorType: "Social Security Fund",
        holdingAmount: 3000000,
        holdingPercentage: 3.0,
        profitDistributed: 73762.5,
        custodialFee: 1475.25,
        paymentMethod: "Domestic Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-07-12",
      },
      {
        custodialAccountId: "CUST-009",
        investorId: "INV-009",
        investorName: "HRDF Investment",
        investorType: "Human Resource Fund",
        holdingAmount: 1500000,
        holdingPercentage: 1.5,
        profitDistributed: 36881.25,
        custodialFee: 737.63,
        paymentMethod: "SWIFT Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-07-15",
      },
      {
        custodialAccountId: "CUST-010",
        investorId: "INV-010",
        investorName: "MTDC Venture Capital",
        investorType: "Development Corporation",
        holdingAmount: 500000,
        holdingPercentage: 0.5,
        profitDistributed: 12293.75,
        custodialFee: 245.88,
        paymentMethod: "Real-time Transfer",
        paymentStatus: "Paid",
        custodyDate: "2024-07-20",
      },
    ],
    investorTypeAnalysis: [
      {
        type: "Government Linked Company",
        count: 45,
        totalHolding: 35000000,
        totalProfit: 860437.5,
      },
      {
        type: "Retirement Fund",
        count: 32,
        totalHolding: 28000000,
        totalProfit: 688650.0,
      },
      {
        type: "Government Pension Fund",
        count: 28,
        totalHolding: 22000000,
        totalProfit: 540925.0,
      },
      {
        type: "Trust Company",
        count: 25,
        totalHolding: 18000000,
        totalProfit: 442575.0,
      },
      {
        type: "Islamic Institution",
        count: 26,
        totalHolding: 15000000,
        totalProfit: 368812.5,
      },
    ],
    custodialServices: {
      totalCustodialFee: 49175.0,
      custodialFeeRate: 0.02,
      servicesProvided: [
        "Asset Safekeeping",
        "Settlement Services",
        "Corporate Actions Processing",
        "Income Collection & Distribution",
        "Regulatory Reporting",
        "Investor Communications",
      ],
    },
  };

  // Removed mockDailyPositionReportData in favor of live API call

  // (Removed mockTransactionFileReportData; now using live secondary transfer data)

  // (Removed mockTradeInstructionFileReportData; trade instruction report now uses live secondary transfer data)

  // Real audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditLogsError, setAuditLogsError] = useState(null);
  const [auditLogsPage, setAuditLogsPage] = useState(1);
  const [auditLogsPageSize, setAuditLogsPageSize] = useState(20);
  const [auditLogsTotal, setAuditLogsTotal] = useState(0);
  const [auditLogsExporting, setAuditLogsExporting] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);
  const { user } = useAuth();

  // Fetch audit logs when audit tab is active
  useEffect(() => {
    if (activeTab !== "audit") return;
    if (!user) return;
    setAuditLogsLoading(true);
    setAuditLogsError(null);
    // Determine filter params
    let params = {
      actorRole: "custodian",
      limit: auditLogsPageSize,
      page: auditLogsPage,
    };
    if (user.id) params.actorId = user.id;
    if (user.parentAccountId) params.parentAccountId = user.parentAccountId;
    // If user is a parent (no parentAccountId), pass only actorId (see backend logic)
    axios
      .get("/api/audit-logs", { params })
      .then((res) => {
        const items = res.data?.items || res.data || [];
        setAuditLogs(items);
        setAuditLogsTotal(res.data?.pagination?.total || items.length);
        setAuditLogsLoading(false);
      })
      .catch(() => {
        setAuditLogsError("Failed to load audit logs");
        setAuditLogsLoading(false);
      });
  }, [activeTab, auditLogsPage, auditLogsPageSize, user]);

  // Wrapper around shared export utility
  const exportCustodianAuditLogsCsv = async () => {
    if (auditLogsExporting) return;
    setAuditLogsExporting(true);
    const result = await exportAllAuditLogsCsv({
      role: "custodian",
      filePrefix: "custodian_audit_logs",
    });
    if (!result.success)
      alert("Failed to export audit logs. Please try again.");
    setAuditLogsExporting(false);
  };

  const tabs = [
    {
      id: "generate",
      name: "Generate Reports",
      icon: FileText,
      description: "Create new reports with custom parameters",
    },
    {
      id: "audit",
      name: "Audit Logs",
      icon: Shield,
      description: "View system audit trails and user activities",
    },
  ];

  // Mock data generator for report content
  const getMockReportData = (reportType) => {
    switch (reportType) {
      case "kyc_status":
        return {
          summary: {
            totalInvestors: 347,
            kycApproved: 298,
            kycPending: 32,
            kycRejected: 17,
            kybApproved: 89,
            kybPending: 12,
            kybRejected: 4,
          },
          details: [
            {
              investorId: "INV-001",
              name: "Ahmad bin Rahman",
              type: "Individual",
              kycStatus: "Approved",
              submissionDate: "2024-01-10",
              approvalDate: "2024-01-12",
              riskRating: "Low",
              documentScore: "95%",
            },
            {
              investorId: "INV-002",
              name: "Tech Solutions Sdn Bhd",
              type: "Corporate",
              kybStatus: "Approved",
              submissionDate: "2024-01-08",
              approvalDate: "2024-01-11",
              riskRating: "Medium",
              documentScore: "92%",
            },
            {
              investorId: "INV-003",
              name: "Jennifer Wong Su Ling",
              type: "Individual",
              kycStatus: "Pending",
              submissionDate: "2024-01-14",
              approvalDate: null,
              riskRating: "Low",
              documentScore: "88%",
            },
            {
              investorId: "INV-004",
              name: "Global Investment Ltd",
              type: "Corporate",
              kybStatus: "Under Review",
              submissionDate: "2024-01-13",
              approvalDate: null,
              riskRating: "High",
              documentScore: "76%",
            },
            {
              investorId: "INV-005",
              name: "Sarah Abdullah",
              type: "Individual",
              kycStatus: "Rejected",
              submissionDate: "2024-01-09",
              approvalDate: null,
              riskRating: "High",
              documentScore: "65%",
              rejectionReason: "Incomplete documentation",
            },
          ],
        };
      case "wallet_holdings":
        return {
          summary: {
            totalWallets: 347,
            activeWallets: 298,
            totalValueMYR: 125000000,
            totalBonds: 15,
            averageHolding: 360000,
          },
          details: [
            {
              walletId: "WALLET-001",
              investorName: "Ahmad bin Rahman",
              walletType: "Hot Wallet",
              totalValueMYR: 250000,
              bondCount: 3,
              lastTransaction: "2024-01-15",
              status: "Active",
            },
            {
              walletId: "WALLET-002",
              investorName: "Tech Solutions Sdn Bhd",
              walletType: "Cold Wallet",
              totalValueMYR: 1500000,
              bondCount: 5,
              lastTransaction: "2024-01-14",
              status: "Active",
            },
            {
              walletId: "WALLET-003",
              investorName: "Jennifer Wong Su Ling",
              walletType: "Hot Wallet",
              totalValueMYR: 180000,
              bondCount: 2,
              lastTransaction: "2024-01-13",
              status: "Active",
            },
            {
              walletId: "WALLET-004",
              investorName: "Global Investment Ltd",
              walletType: "Cold Wallet",
              totalValueMYR: 2200000,
              bondCount: 7,
              lastTransaction: "2024-01-12",
              status: "Active",
            },
            {
              walletId: "WALLET-005",
              investorName: "Retirement Fund Malaysia",
              walletType: "Cold Wallet",
              totalValueMYR: 5000000,
              bondCount: 12,
              lastTransaction: "2024-01-11",
              status: "Active",
            },
          ],
        };
      case "transaction_history":
        return {
          summary: {
            totalTransactions: 2847,
            totalValueMYR: 325000000,
            successfulTransactions: 2798,
            failedTransactions: 49,
            averageTransactionSize: 114000,
          },
          details: [
            {
              transactionId: "TXN-2024-001",
              date: "2024-01-15",
              time: "14:30:45",
              type: "Token Distribution",
              fromWallet: "CUSTODIAN-MAIN",
              toWallet: "WALLET-001",
              bondName: "Green Infrastructure Bond 2024-A",
              amount: 50000,
              status: "Completed",
            },
            {
              transactionId: "TXN-2024-002",
              date: "2024-01-15",
              time: "11:22:12",
              type: "Interest Payment",
              fromWallet: "PAYMENT-POOL",
              toWallet: "WALLET-002",
              bondName: "Tech Development Sukuk",
              amount: 12500,
              status: "Completed",
            },
            {
              transactionId: "TXN-2024-003",
              date: "2024-01-14",
              time: "16:45:33",
              type: "Subscription Payment",
              fromWallet: "WALLET-003",
              toWallet: "SUBSCRIPTION-POOL",
              bondName: "Infrastructure Bond 2022-A",
              amount: 100000,
              status: "Completed",
            },
            {
              transactionId: "TXN-2024-004",
              date: "2024-01-14",
              time: "09:18:55",
              type: "Refund",
              fromWallet: "REFUND-POOL",
              toWallet: "WALLET-004",
              bondName: "Corporate Expansion Bond",
              amount: 75000,
              status: "Completed",
            },
            {
              transactionId: "TXN-2024-005",
              date: "2024-01-13",
              time: "13:55:21",
              type: "Token Transfer",
              fromWallet: "WALLET-005",
              toWallet: "WALLET-006",
              bondName: "Renewable Energy Sukuk Series B",
              amount: 200000,
              status: "Failed",
              failureReason: "Insufficient balance",
            },
          ],
        };
      case "distribution_report":
        return {
          summary: {
            totalDistributions: 156,
            totalAmountMYR: 28500000,
            upcomingDistributions: 8,
            completedDistributions: 148,
            averageDistribution: 182000,
          },
          details: [
            {
              distributionId: "DIST-2024-001",
              bondName: "Green Infrastructure Bond 2024-A",
              distributionDate: "2024-01-15",
              type: "Quarterly Interest",
              totalAmount: 2500000,
              recipientCount: 45,
              status: "Completed",
              rate: "5.25%",
            },
            {
              distributionId: "DIST-2024-002",
              bondName: "Tech Development Sukuk",
              distributionDate: "2024-01-20",
              type: "Profit Distribution",
              totalAmount: 1800000,
              recipientCount: 32,
              status: "Scheduled",
              rate: "4.75%",
            },
            {
              distributionId: "DIST-2024-003",
              bondName: "Infrastructure Bond 2022-A",
              distributionDate: "2024-01-10",
              type: "Semi-annual Interest",
              totalAmount: 3200000,
              recipientCount: 67,
              status: "Completed",
              rate: "6.00%",
            },
            {
              distributionId: "DIST-2024-004",
              bondName: "Corporate Expansion Bond",
              distributionDate: "2024-01-25",
              type: "Monthly Interest",
              totalAmount: 890000,
              recipientCount: 23,
              status: "Pending Approval",
              rate: "4.25%",
            },
            {
              distributionId: "DIST-2024-005",
              bondName: "Renewable Energy Sukuk Series B",
              distributionDate: "2024-01-05",
              type: "Profit Distribution",
              totalAmount: 4100000,
              recipientCount: 89,
              status: "Completed",
              rate: "5.75%",
            },
          ],
        };
      case "compliance_report":
        return {
          summary: {
            totalScreenings: 1247,
            clearScreenings: 1198,
            flaggedCases: 35,
            underInvestigation: 14,
            resolvedCases: 21,
            complianceRate: "96.1%",
          },
          details: [
            {
              screeningId: "AML-2024-001",
              investorName: "Ahmad bin Rahman",
              screeningType: "AML Screening",
              date: "2024-01-15",
              result: "Clear",
              riskScore: 15,
              status: "Approved",
            },
            {
              screeningId: "AML-2024-002",
              investorName: "Tech Solutions Sdn Bhd",
              screeningType: "Enhanced Due Diligence",
              date: "2024-01-14",
              result: "Clear",
              riskScore: 28,
              status: "Approved",
            },
            {
              screeningId: "AML-2024-003",
              investorName: "Global Investment Ltd",
              screeningType: "PEP Screening",
              date: "2024-01-13",
              result: "Flagged",
              riskScore: 75,
              status: "Under Investigation",
              flagReason: "Potential PEP connection",
            },
            {
              screeningId: "AML-2024-004",
              investorName: "Jennifer Wong Su Ling",
              screeningType: "Transaction Monitoring",
              date: "2024-01-12",
              result: "Clear",
              riskScore: 22,
              status: "Approved",
            },
            {
              screeningId: "AML-2024-005",
              investorName: "Offshore Holdings Inc",
              screeningType: "Sanctions Screening",
              date: "2024-01-11",
              result: "Flagged",
              riskScore: 85,
              status: "Investigation Complete",
              flagReason: "High-risk jurisdiction",
              resolution: "Additional documentation provided and approved",
            },
          ],
        };
      default:
        return {
          summary: { message: "Report data not available" },
          details: [],
        };
    }
  };

  const handleGenerateReport = () => {
    if (!selectedReport) {
      console.warn("No report type selected");
      return;
    }

    console.log("Generating report:", selectedReport);
    setIsGenerating(true);

    // Simulate report generation
    setTimeout(() => {
      const newReport = {
        id: `RPT-${String(reportsHistory.length + 1).padStart(3, "0")}`,
        reportType: reportTypes.find((r) => r.id === selectedReport)?.name,
        generatedDate: new Date().toISOString(),
        dateRange:
          dateRange === "custom"
            ? "Custom Range"
            : dateRange.replace(/([a-z])([A-Z])/g, "$1 $2"),
        parameters: {
          startDate:
            dateRange === "custom" ? customStartDate : getDateRangeStart(),
          endDate:
            dateRange === "custom"
              ? customEndDate
              : new Date().toISOString().split("T")[0],
          investor:
            filterInvestor === "all"
              ? "All Investors"
              : investors.find((i) => i.id === filterInvestor)?.name,
          bond: filterBond === "all" ? "All Bonds" : filterBond,
        },
        status: "completed",
        downloadUrl: "#",
        generatedBy: "custodian.admin@bank.com",
        fileSize: `${Math.floor(Math.random() * 5) + 1}.${Math.floor(
          Math.random() * 9
        )} MB`,
      };

      setGeneratedReports([newReport, ...generatedReports]);
      setIsGenerating(false);

      // Show report preview for specific reports
      if (selectedReport === "daily_profit_accrual") {
        setSelectedReportMeta(newReport);
        setSelectedReportData(mockCustodianDailyProfitAccrualData);
        setShowReportModal(true);
      } else if (selectedReport === "investor_distribution") {
        setSelectedReportMeta(newReport);
        setSelectedReportData(mockCustodianInvestorDistributionData);
        setShowReportModal(true);
      } else if (selectedReport === "daily_position") {
        setSelectedReportMeta(newReport);
        // Fetch real Daily Position data
        custodianAPI
          .getDailyPositionReport()
          .then((resp) => {
            const data = resp?.data?.data || resp?.data; // Backend shape { success, data }
            if (data?.positions) {
              setSelectedReportData(data);
            } else {
              setSelectedReportData({
                reportTitle: "Daily Position Report",
                reportPeriod: new Date().toISOString().split("T")[0],
                positionDate: new Date().toLocaleDateString("en-GB"),
                positions: [],
                summary: {
                  totalPositions: 0,
                  totalSafekeepingPosition: 0,
                  totalConventionalBonds: 0,
                  totalSukuk: 0,
                  uniqueAccounts: 0,
                  uniqueSecurities: 0,
                },
              });
            }
            setShowReportModal(true);
          })
          .catch((err) => {
            console.error("Failed to load daily position report:", err);
            setSelectedReportData({
              reportTitle: "Daily Position Report (Error)",
              reportPeriod: new Date().toISOString().split("T")[0],
              positionDate: new Date().toLocaleDateString("en-GB"),
              positions: [],
              summary: {
                totalPositions: 0,
                totalSafekeepingPosition: 0,
                totalConventionalBonds: 0,
                totalSukuk: 0,
                uniqueAccounts: 0,
                uniqueSecurities: 0,
              },
              error: "Unable to fetch report data.",
            });
            setShowReportModal(true);
          });
      } else if (selectedReport === "transaction_file") {
        setSelectedReportMeta(newReport);
        // Fetch real transaction file data
        fetchReportData({ reportType: "Transaction File" })
          .then((data) => {
            setSelectedReportData(
              data || {
                reportTitle: "Transaction File Report",
                transactions: [],
                summary: {},
              }
            );
            setShowReportModal(true);
          })
          .catch((err) => {
            console.error("Failed to load transaction file report:", err);
            setSelectedReportData({
              reportTitle: "Transaction File Report (Error)",
              transactions: [],
              summary: {},
              error: "Unable to fetch transaction file data.",
            });
            setShowReportModal(true);
          });
      } else if (selectedReport === "trade_instruction_file") {
        setSelectedReportMeta(newReport);
        // Fetch real trade instruction data (secondary transfers)
        fetchReportData({ reportType: "Trade Instruction File" })
          .then((data) => {
            setSelectedReportData(
              data || {
                reportTitle: "Trade Instruction File Report",
                tradeInstructions: [],
                summary: {},
              }
            );
            setShowReportModal(true);
          })
          .catch((err) => {
            console.error("Failed to load trade instruction file report:", err);
            setSelectedReportData({
              reportTitle: "Trade Instruction File Report (Error)",
              tradeInstructions: [],
              summary: {},
              error: "Unable to fetch trade instruction data.",
            });
            setShowReportModal(true);
          });
      } else {
        // For other report types without specific preview, just show success
        console.log("Report generated successfully:", newReport);
        // Optionally show a toast or notification here
      }
    }, 3000);
  };

  const handleViewReport = async (report) => {
    const data = await fetchReportData(report);
    if (!data) {
      console.warn("No dataset available for report", report?.reportType);
      return;
    }
    setSelectedReportMeta(report);
    setSelectedReportData(data);
    setShowReportModal(true);
  };

  const exportReport = (report, data, format) => {
    if (!report || !data) return;
    if (format === "csv") {
      exportToCSV(report, data);
    } else {
      exportToPDF(report, data);
    }
  };

  const handleDownloadReport = async (report, format) => {
    const isCurrentSelection =
      selectedReportMeta?.id &&
      report?.id &&
      selectedReportMeta.id === report.id;
    let data = isCurrentSelection ? selectedReportData : null;

    if (!data) {
      data = await fetchReportData(report);
    }

    if (!data) {
      console.warn("Unable to download report because no data was returned.");
      return;
    }

    exportReport(report, data, format);
  };

  const handleDownloadCurrentReport = (format) => {
    if (!selectedReportMeta || !selectedReportData) {
      console.warn("No report selected for download.");
      return;
    }
    exportReport(selectedReportMeta, selectedReportData, format);
  };

  const handleGenerateSummary = async () => {
    if (!selectedReportData) {
      console.warn("No report data available for summary generation.");
      return;
    }

    setIsGeneratingSummary(true);

    try {
      // Call ollama API through backend proxy to avoid CORS issues
      const response = await custodianAPI.generateSummary({
        reportTitle: selectedReportData.reportTitle,
        reportData: selectedReportData,
      });

      if (response.data.success) {
        setReportSummary(response.data.data.summary);
      } else {
        throw new Error(response.data.message || "Failed to generate summary");
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      alert(
        "Failed to generate summary. Please try again. Error: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const getDateRangeStart = () => {
    const today = new Date();
    switch (dateRange) {
      case "last7days":
        return new Date(today.setDate(today.getDate() - 7))
          .toISOString()
          .split("T")[0];
      case "last30days":
        return new Date(today.setDate(today.getDate() - 30))
          .toISOString()
          .split("T")[0];
      case "last90days":
        return new Date(today.setDate(today.getDate() - 90))
          .toISOString()
          .split("T")[0];
      default:
        return new Date().toISOString().split("T")[0];
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAuditCategoryBadge = (category) => {
    const categoryClasses = {
      KYC_MANAGEMENT: "bg-blue-100 text-blue-800",
      WALLET_MANAGEMENT: "bg-purple-100 text-purple-800",
      TOKEN_DISTRIBUTION: "bg-green-100 text-green-800",
      PAYMENT_PROCESSING: "bg-yellow-100 text-yellow-800",
      AUTHENTICATION: "bg-red-100 text-red-800",
      SYSTEM: "bg-gray-100 text-gray-800",
    };
    return categoryClasses[category] || "bg-gray-100 text-gray-800";
  };

  const getResultBadge = (result) => {
    return result === "SUCCESS"
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div>
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Reporting & Audit Trails
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Generate comprehensive reports and access detailed audit logs
                for custodial operations
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Navigation Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex -mb-px space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Generate Reports Tab */}
        {activeTab === "generate" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Report Selection */}
              <div className="lg:col-span-2">
                <div className="p-6 bg-white rounded-lg shadow">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">
                    Select Report Type
                  </h3>
                  <div className="space-y-3">
                    {reportTypes && reportTypes.length > 0 ? (
                      reportTypes.map((report) => {
                        const Icon = report.icon;
                        return (
                          <label
                            key={report.id}
                            className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                              selectedReport === report.id
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <input
                              type="radio"
                              name="reportType"
                              value={report.id}
                              checked={selectedReport === report.id}
                              onChange={(e) =>
                                setSelectedReport(e.target.value)
                              }
                              className="mt-1 mr-3"
                            />
                            <Icon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                {report.name}
                              </div>
                              <div className="mt-1 text-sm text-gray-500">
                                {report.description}
                              </div>
                              <div className="mt-1 text-xs text-gray-400">
                                Estimated generation time:{" "}
                                {report.estimatedTime}
                              </div>
                            </div>
                          </label>
                        );
                      })
                    ) : (
                      <div className="py-8 text-center text-gray-500">
                        <p>
                          No report types available. Report types will be loaded
                          dynamically.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Report Parameters */}
              <div className="space-y-6">
                {/* Date Range */}
                <div className="p-6 bg-white rounded-lg shadow">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">
                    Date Range
                  </h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last7days"
                        checked={dateRange === "last7days"}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 7 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last30days"
                        checked={dateRange === "last30days"}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 30 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="last90days"
                        checked={dateRange === "last90days"}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Last 90 Days</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="dateRange"
                        value="custom"
                        checked={dateRange === "custom"}
                        onChange={(e) => setDateRange(e.target.value)}
                        className="mr-2"
                      />
                      <span className="text-sm">Custom Range</span>
                    </label>

                    {dateRange === "custom" && (
                      <div className="mt-3 space-y-2">
                        <div>
                          <label className="block mb-1 text-xs font-medium text-gray-700">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block mb-1 text-xs font-medium text-gray-700">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Filters */}
                <div className="p-6 bg-white rounded-lg shadow">
                  <h3 className="mb-4 text-lg font-medium text-gray-900">
                    Filters
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Investor
                      </label>
                      <select
                        value={filterInvestor}
                        onChange={(e) => setFilterInvestor(e.target.value)}
                        className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="all">All Investors</option>
                        {investors.map((investor) => (
                          <option key={investor.id} value={investor.id}>
                            {investor.name} ({investor.type})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Bond
                      </label>
                      <select
                        value={filterBond}
                        onChange={(e) => setFilterBond(e.target.value)}
                        className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="all">All Bonds</option>
                        {bonds.map((bond) => (
                          <option key={bond} value={bond}>
                            {bond}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="p-6 bg-white rounded-lg shadow">
                  <button
                    onClick={handleGenerateReport}
                    disabled={!selectedReport || isGenerating}
                    className="w-full btn-primary"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Generate Report
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === "audit" && (
          <div className="space-y-6">
            <div className="overflow-hidden overflow-x-auto bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    System Audit Trail
                  </h3>
                  <button
                    type="button"
                    disabled={auditLogsLoading || auditLogsExporting}
                    onClick={exportCustodianAuditLogsCsv}
                    className="btn-primary"
                  >
                    {auditLogsExporting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                      </>
                    )}
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Comprehensive log of all custodian actions, investor data
                  changes, and financial transactions.
                </p>
                <div className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium text-blue-800 bg-blue-100 rounded">
                  Actor role: Custodian
                </div>
                {auditLogsExporting && (
                  <div className="mt-2">
                    <span className="text-xs text-gray-500">
                      Gathering all pages of audit logs...
                    </span>
                  </div>
                )}
              </div>
              {auditLogsLoading ? (
                <div className="p-8 text-center text-gray-500">
                  Loading audit logs...
                </div>
              ) : auditLogsError ? (
                <div className="p-8 text-center text-red-500">
                  {auditLogsError}
                </div>
              ) : (
                <>
                  <table className="table">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Timestamp</th>
                        <th className="table-header-cell">User</th>
                        <th className="table-header-cell">Action</th>
                        <th className="table-header-cell">Module</th>
                        <th className="table-header-cell">Result</th>
                        <th className="table-header-cell">Details</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {auditLogs.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="table-cell">
                            No audit logs found.
                          </td>
                        </tr>
                      ) : (
                        auditLogs.map((log) => (
                          <tr
                            key={log._id || log.id}
                            className="hover:bg-gray-50"
                          >
                            <td className="table-cell">
                              {formatDateTime(log.timestamp || log.createdAt)}
                            </td>
                            <td className="table-cell">
                              {typeof log.actorId === "string"
                                ? log.actorId
                                : log.actorId?.email || "Unknown User"}
                            </td>
                            <td className="table-cell">
                              <div className="text-sm font-medium text-gray-900">
                                {log.action}
                              </div>
                            </td>
                            <td className="table-cell">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800`}
                              >
                                {log.module}
                              </span>
                            </td>
                            <td className="table-cell">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getResultBadge(
                                  (log.status || "").toUpperCase()
                                )}`}
                              >
                                {(log.status || "").toUpperCase() ===
                                "SUCCESS" ? (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                ) : (
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                )}
                                {(log.status || "").toUpperCase()}
                              </span>
                            </td>
                            <td className="table-cell">
                              {log.details ? (
                                <button
                                  className="p-1 text-primary-600 hover:text-primary-700"
                                  title="View Details"
                                  onClick={() => {
                                    setSelectedAuditLog(log);
                                    setShowAuditLogModal(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {/* Pagination Controls */}
                  <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="text-sm text-gray-600">
                      {auditLogsTotal > 0 && (
                        <span>
                          Showing{" "}
                          {Math.min(
                            (auditLogsPage - 1) * auditLogsPageSize + 1,
                            auditLogsTotal
                          )}{" "}
                          to{" "}
                          {Math.min(
                            auditLogsPage * auditLogsPageSize,
                            auditLogsTotal
                          )}{" "}
                          of {auditLogsTotal} logs
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                        onClick={() =>
                          setAuditLogsPage((p) => Math.max(1, p - 1))
                        }
                        disabled={auditLogsLoading || auditLogsPage <= 1}
                      >
                        Prev
                      </button>
                      {Array.from({
                        length: Math.max(
                          1,
                          Math.ceil(auditLogsTotal / auditLogsPageSize)
                        ),
                      })
                        .slice(
                          Math.max(0, auditLogsPage - 3),
                          Math.max(0, auditLogsPage - 3) + 5
                        )
                        .map((_, idx) => {
                          const pageNum = Math.max(1, auditLogsPage - 3) + idx;
                          const pages = Math.ceil(
                            auditLogsTotal / auditLogsPageSize
                          );
                          if (pageNum > pages) return null;
                          const isActive = pageNum === auditLogsPage;
                          return (
                            <button
                              key={pageNum}
                              className={`px-3 py-1.5 text-sm rounded border ${
                                isActive
                                  ? "border-blue-500 bg-blue-50 text-blue-700"
                                  : "border-gray-300 bg-white text-gray-700"
                              }`}
                              onClick={() => setAuditLogsPage(pageNum)}
                              disabled={auditLogsLoading}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      <button
                        className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                        onClick={() =>
                          setAuditLogsPage((p) =>
                            Math.min(
                              Math.ceil(auditLogsTotal / auditLogsPageSize) ||
                                p,
                              p + 1
                            )
                          )
                        }
                        disabled={
                          auditLogsLoading ||
                          (Math.ceil(auditLogsTotal / auditLogsPageSize) > 0 &&
                            auditLogsPage >=
                              Math.ceil(auditLogsTotal / auditLogsPageSize))
                        }
                      >
                        Next
                      </button>
                      <select
                        className="px-2 py-1 ml-2 text-sm border border-gray-300 rounded"
                        value={auditLogsPageSize}
                        onChange={(e) => {
                          setAuditLogsPage(1);
                          setAuditLogsPageSize(Number(e.target.value));
                        }}
                        disabled={auditLogsLoading}
                      >
                        {[10, 20, 50, 100].map((size) => (
                          <option key={size} value={size}>
                            {size}/page
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {showAuditLogModal && selectedAuditLog && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                      <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <h2 className="text-lg font-medium text-gray-900">
                              Audit Log Details
                            </h2>
                            <button
                              onClick={() => {
                                setShowAuditLogModal(false);
                                setSelectedAuditLog(null);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-6 h-6" />
                            </button>
                          </div>
                        </div>
                        <div className="px-6 py-4">
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium">Timestamp:</span>{" "}
                              {formatDateTime(
                                selectedAuditLog.timestamp ||
                                  selectedAuditLog.createdAt
                              )}
                            </div>
                            <div>
                              <span className="font-medium">User:</span>{" "}
                              {formatActor(selectedAuditLog.actorId)}
                            </div>
                            <div>
                              <span className="font-medium">Action:</span>{" "}
                              {selectedAuditLog.action}
                            </div>
                            <div>
                              <span className="font-medium">Category:</span>{" "}
                              {selectedAuditLog.module?.replace("_", " ") || ""}
                            </div>
                            <div>
                              <span className="font-medium">Result:</span>{" "}
                              {(selectedAuditLog.status || "").toUpperCase()}
                            </div>
                            <div>
                              <span className="font-medium">IP Address:</span>{" "}
                              {selectedAuditLog.ip ||
                                selectedAuditLog.ipAddress ||
                                ""}
                            </div>
                            <div>
                              <span className="font-medium">Details:</span>{" "}
                              {selectedAuditLog.details?.description || ""}
                            </div>
                            <div>
                              <span className="font-medium">Target:</span>{" "}
                              {(() => {
                                const rawType =
                                  selectedAuditLog.targetType ||
                                  selectedAuditLog.target?.type ||
                                  "";
                                const rawTargetId =
                                  selectedAuditLog.targetId !== undefined
                                    ? selectedAuditLog.targetId
                                    : selectedAuditLog.target?.id !== undefined
                                    ? selectedAuditLog.target?.id
                                    : selectedAuditLog.target?._id;
                                let targetId = "";
                                if (
                                  typeof rawTargetId === "object" &&
                                  rawTargetId !== null
                                ) {
                                  targetId =
                                    rawTargetId._id ||
                                    rawTargetId.id ||
                                    rawTargetId.code ||
                                    rawTargetId.name ||
                                    JSON.stringify(rawTargetId);
                                } else if (typeof rawTargetId !== "undefined") {
                                  targetId = String(rawTargetId);
                                }
                                if (rawType && targetId) {
                                  return (
                                    <>
                                      <span className="px-1 font-mono text-xs bg-gray-100 rounded">
                                        {targetId}
                                      </span>
                                      <span className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                        {rawType}
                                      </span>
                                    </>
                                  );
                                } else if (targetId) {
                                  return (
                                    <span className="px-1 font-mono text-xs bg-gray-100 rounded">
                                      {targetId}
                                    </span>
                                  );
                                } else if (rawType) {
                                  return (
                                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                      {rawType}
                                    </span>
                                  );
                                }
                                return "N/A";
                              })()}
                            </div>
                            {(() => {
                              const rawType =
                                selectedAuditLog.targetType ||
                                selectedAuditLog.target?.type ||
                                "";
                              const targetType = normalizeTargetType(rawType);
                              const rawTargetId =
                                selectedAuditLog.targetId !== undefined
                                  ? selectedAuditLog.targetId
                                  : selectedAuditLog.target?.id !== undefined
                                  ? selectedAuditLog.target?.id
                                  : selectedAuditLog.target?._id;
                              let targetId = "";
                              if (
                                typeof rawTargetId === "object" &&
                                rawTargetId !== null
                              ) {
                                targetId =
                                  rawTargetId._id ||
                                  rawTargetId.id ||
                                  rawTargetId.code ||
                                  rawTargetId.name ||
                                  JSON.stringify(rawTargetId);
                              } else if (typeof rawTargetId !== "undefined") {
                                targetId = String(rawTargetId);
                              }
                              if (
                                targetType &&
                                targetId &&
                                targetTypeInfo[targetType]
                              ) {
                                return (
                                  <div className="mt-1 text-xs text-gray-600">
                                    <span className="font-semibold">
                                      {targetTypeInfo[targetType].label}:
                                    </span>{" "}
                                    {targetTypeInfo[targetType].description}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {selectedAuditLog.target && (
                              <div className="mt-3">
                                <div className="font-medium text-gray-900">
                                  Target Details
                                </div>
                                {(() => {
                                  const t = selectedAuditLog.target;
                                  const rawType =
                                    selectedAuditLog.targetType ||
                                    selectedAuditLog.target?.type ||
                                    "";
                                  const targetType =
                                    normalizeTargetType(rawType);
                                  if (
                                    targetType &&
                                    schemaFormatters[targetType]
                                  ) {
                                    return schemaFormatters[targetType](t);
                                  }
                                  const exclude = new Set([
                                    "_id",
                                    "__v",
                                    "createdAt",
                                    "updatedAt",
                                    "password",
                                    "secret",
                                    "hash",
                                  ]);
                                  const entries = Object.entries(t).filter(
                                    ([k]) => !exclude.has(k)
                                  );
                                  if (!entries.length)
                                    return (
                                      <div className="mt-1 text-xs text-gray-500">
                                        No details available.
                                      </div>
                                    );
                                  return (
                                    <div className="mt-2">
                                      <ul className="text-sm text-gray-700 list-disc list-inside">
                                        {entries.map(([key, value]) => (
                                          <li key={key}>
                                            <span className="font-semibold">
                                              {key}:
                                            </span>{" "}
                                            {formatValue(value)}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            {selectedAuditLog.details &&
                              Object.keys(selectedAuditLog.details).filter(
                                (k) => k !== "description"
                              ).length > 0 && (
                                <div className="mt-2">
                                  <span className="font-medium">
                                    Additional Details:
                                  </span>
                                  <ul className="list-disc list-inside">
                                    {Object.entries(selectedAuditLog.details)
                                      .filter(([k]) => k !== "description")
                                      .map(([key, value]) => (
                                        <li key={key}>
                                          <span className="font-semibold">
                                            {key}:
                                          </span>{" "}
                                          {formatValue(value)}
                                        </li>
                                      ))}
                                  </ul>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Report Preview Modal */}
      {showReportModal && selectedReportData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-secondary-900">
                {selectedReportData.reportTitle}
              </h2>
              <button
                onClick={() => setShowReportModal(false)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Report Header */}
            <div className="p-4 mb-6 rounded-lg bg-gray-50">
              {selectedReportData.tradeInstructions ? (
                // Trade Instruction File Report Header
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Report Period
                    </span>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedReportData.reportPeriod}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Instructions
                    </span>
                    <p className="text-lg font-semibold text-primary-600">
                      {selectedReportData.summary?.totalInstructions ?? 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Traded Value
                    </span>
                    <p className="text-lg font-semibold text-success-600">
                      MYR{" "}
                      {(
                        selectedReportData.summary?.totalTradedValue ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Buy Orders
                    </span>
                    <p className="text-lg font-semibold text-blue-600">
                      {selectedReportData.summary?.buyOrders ?? 0}
                    </p>
                  </div>
                </div>
              ) : selectedReportData.transactions ? (
                // Transaction File Report Header
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Report Period
                    </span>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedReportData.reportPeriod}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Transactions
                    </span>
                    <p className="text-lg font-semibold text-primary-600">
                      {selectedReportData.summary?.totalTransactions ?? 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Settlement Amount
                    </span>
                    <p className="text-lg font-semibold text-success-600">
                      MYR{" "}
                      {(
                        selectedReportData.summary?.totalSettlementAmount ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Completed Transactions
                    </span>
                    <p className="text-lg font-semibold text-purple-600">
                      {selectedReportData.summary?.completedTransactions ?? 0}
                    </p>
                  </div>
                </div>
              ) : selectedReportData.positions ? (
                // Daily Position Report Header
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Position Date
                    </span>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedReportData.positionDate}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Positions
                    </span>
                    <p className="text-lg font-semibold text-primary-600">
                      {selectedReportData.summary?.totalPositions ?? 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Safekeeping Position
                    </span>
                    <p className="text-lg font-semibold text-success-600">
                      {(
                        selectedReportData.summary?.totalSafekeepingPosition ??
                        0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Unique Accounts
                    </span>
                    <p className="text-lg font-semibold text-purple-600">
                      {selectedReportData.summary?.uniqueAccounts ?? 0}
                    </p>
                  </div>
                </div>
              ) : selectedReportData.investorHoldings ? (
                // Investor Distribution Report Header
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Bond Name
                    </span>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedReportData.bondName}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Report Period
                    </span>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedReportData.reportPeriod}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Profit Distributed
                    </span>
                    <p className="text-lg font-semibold text-success-600">
                      RM{" "}
                      {(
                        selectedReportData.summary?.totalProfitDistributed ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Custodial Fee Earned
                    </span>
                    <p className="text-lg font-semibold text-purple-600">
                      RM{" "}
                      {(
                        selectedReportData.summary?.custodialFeeEarned ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              ) : (
                // Daily Profit Accrual Report Header
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Report Period
                    </span>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedReportData.reportPeriod}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Custodial Assets
                    </span>
                    <p className="text-lg font-semibold text-primary-600">
                      RM{" "}
                      {(
                        selectedReportData.totalCustodialAssets / 1000000
                      ).toFixed(1)}
                      M
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Accrued Profit
                    </span>
                    <p className="text-lg font-semibold text-success-600">
                      RM{" "}
                      {selectedReportData.totalAccruedProfit.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Investor Accounts
                    </span>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedReportData.totalInvestorAccounts}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Section */}
            {selectedReportData.tradeInstructions ? (
              // Trade Instruction File Report Summary
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Trade Instructions Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-6">
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Total Instructions
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedReportData.summary?.totalInstructions ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Buy Orders
                    </span>
                    <p className="text-sm font-semibold text-blue-600">
                      {selectedReportData.summary?.buyOrders ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Sell Orders
                    </span>
                    <p className="text-sm font-semibold text-red-600">
                      {selectedReportData.summary?.sellOrders ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Total Clearing Fees
                    </span>
                    <p className="text-sm font-semibold text-orange-600">
                      MYR{" "}
                      {(
                        selectedReportData.summary?.totalClearingFees ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Total Brokerage Fees
                    </span>
                    <p className="text-sm font-semibold text-purple-600">
                      MYR{" "}
                      {(
                        selectedReportData.summary?.totalBrokerageFees ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Unique Counterparties
                    </span>
                    <p className="text-sm font-semibold text-green-600">
                      {selectedReportData.summary?.uniqueCounterparties ?? 0}
                    </p>
                  </div>
                </div>
              </div>
            ) : selectedReportData.transactions ? (
              // Transaction File Report Summary
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Transaction Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Total Transactions
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedReportData.summary?.totalTransactions ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Completed
                    </span>
                    <p className="text-sm font-semibold text-green-600">
                      {selectedReportData.summary?.completedTransactions ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Pending
                    </span>
                    <p className="text-sm font-semibold text-yellow-600">
                      {selectedReportData.summary?.pendingTransactions ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Unique Counterparties
                    </span>
                    <p className="text-sm font-semibold text-blue-600">
                      {selectedReportData.summary?.uniqueCounterparties ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Unique Securities
                    </span>
                    <p className="text-sm font-semibold text-purple-600">
                      {selectedReportData.summary?.uniqueSecurities ?? 0}
                    </p>
                  </div>
                </div>
              </div>
            ) : selectedReportData.positions ? (
              // Daily Position Report Summary
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Position Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Total Positions
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedReportData.summary?.totalPositions ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Conventional Bonds
                    </span>
                    <p className="text-sm font-semibold text-blue-600">
                      {(
                        selectedReportData.summary?.totalConventionalBonds ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Sukuk Holdings
                    </span>
                    <p className="text-sm font-semibold text-green-600">
                      {(
                        selectedReportData.summary?.totalSukuk ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Unique Accounts
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedReportData.summary?.uniqueAccounts ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Unique Securities
                    </span>
                    <p className="text-sm font-semibold text-purple-600">
                      {selectedReportData.summary?.uniqueSecurities ?? 0}
                    </p>
                  </div>
                </div>
              </div>
            ) : selectedReportData.investorHoldings ? (
              // Investor Distribution Report Summary
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Distribution Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Total Investors
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedReportData.summary?.totalInvestors ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Assets Under Custody
                    </span>
                    <p className="text-sm font-semibold text-primary-600">
                      RM{" "}
                      {(
                        (selectedReportData.summary?.totalAssetsUnderCustody ??
                          0) / 1000000
                      ).toFixed(1)}
                      M
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Average Profit per Investor
                    </span>
                    <p className="text-sm font-semibold text-success-600">
                      RM{" "}
                      {(
                        selectedReportData.summary?.averageProfitPerInvestor ??
                        0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Distribution Date
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedReportData.summary?.distributionDate || "-"}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Payment Status
                    </span>
                    <p className="text-sm font-semibold text-green-600">
                      {selectedReportData.summary?.paymentStatus || "-"}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              // Daily Profit Accrual Report Summary
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Custodial Summary
                </h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Sukuk Holdings
                    </span>
                    <p className="text-sm font-semibold text-green-600">
                      RM{" "}
                      {(
                        (selectedReportData.summary?.totalSukukHoldings ?? 0) /
                        1000000
                      ).toFixed(1)}
                      M
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Conventional Bonds
                    </span>
                    <p className="text-sm font-semibold text-blue-600">
                      RM{" "}
                      {(
                        (selectedReportData.summary?.totalConventionalBonds ??
                          0) / 1000000
                      ).toFixed(1)}
                      M
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Avg Daily Accrual
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      RM{" "}
                      {(
                        selectedReportData.summary?.averageDailyAccrual ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Days in Period
                    </span>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedReportData.summary?.totalDaysInPeriod ?? 0}
                    </p>
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded">
                    <span className="text-xs font-medium text-gray-500">
                      Custody Fee Earned
                    </span>
                    <p className="text-sm font-semibold text-purple-600">
                      RM{" "}
                      {(
                        selectedReportData.summary?.custodyFeeEarned ?? 0
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Investor Holdings Table - for Investor Distribution Report */}
            {selectedReportData.investorHoldings && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Custodial Holdings & Profit Distribution
                </h3>
                <div className="overflow-x-auto">
                  <table className="table border border-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Custodial Account</th>
                        <th className="table-header-cell">Investor Name</th>
                        <th className="table-header-cell">Type</th>
                        <th className="table-header-cell">Holding Amount</th>
                        <th className="table-header-cell">Holding %</th>
                        <th className="table-header-cell">
                          Profit Distributed
                        </th>
                        <th className="table-header-cell">Custodial Fee</th>
                        <th className="table-header-cell">Payment Method</th>
                        <th className="table-header-cell">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {selectedReportData.investorHoldings.map(
                        (holding, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="px-4 py-2 font-mono text-sm text-gray-900">
                              {holding.custodialAccountId}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {holding.investorName}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span className="inline-flex px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                                {holding.investorType}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-900">
                              RM {holding.holdingAmount.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-900">
                              {holding.holdingPercentage}%
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-right text-success-600">
                              RM {holding.profitDistributed.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-right text-purple-600">
                              RM {holding.custodialFee.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {holding.paymentMethod}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              <span className="inline-flex px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                                {holding.paymentStatus}
                              </span>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Investor Type Analysis Table - for Investor Distribution Report */}
            {selectedReportData.investorTypeAnalysis && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Investor Type Analysis
                </h3>
                <div className="overflow-x-auto">
                  <table className="table border border-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Investor Type</th>
                        <th className="table-header-cell">Count</th>
                        <th className="table-header-cell">Total Holding</th>
                        <th className="table-header-cell">Total Profit</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {selectedReportData.investorTypeAnalysis.map(
                        (type, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {type.type}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-900">
                              {type.count}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-900">
                              RM {type.totalHolding.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-right text-success-600">
                              RM {type.totalProfit.toLocaleString()}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Bond Breakdown Table - for Daily Profit Accrual Report */}
            {selectedReportData.bondBreakdown && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Bond Portfolio Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="table border border-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Bond Name</th>
                        <th className="table-header-cell">Type</th>
                        <th className="table-header-cell">
                          Outstanding Amount
                        </th>
                        <th className="table-header-cell">Investors</th>
                        <th className="table-header-cell">Total Accrued</th>
                        <th className="table-header-cell">Custody Fee</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {selectedReportData.bondBreakdown.map((bond, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {bond.bondName}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                bond.bondType === "Sukuk"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {bond.bondType}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900">
                            RM {bond.outstandingAmount.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm text-right text-gray-900">
                            {bond.investors}
                          </td>
                          <td className="px-4 py-2 text-sm font-medium text-right text-success-600">
                            RM {bond.totalAccrued.toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-sm font-medium text-right text-purple-600">
                            RM {bond.custodyFee.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Investor Accounts Table - for Daily Profit Accrual Report */}
            {selectedReportData.topInvestorAccounts && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Top Custodial Accounts
                </h3>
                <div className="overflow-x-auto">
                  <table className="table border border-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Account ID</th>
                        <th className="table-header-cell">Investor Name</th>
                        <th className="table-header-cell">Total Holdings</th>
                        <th className="table-header-cell">Accrued Profit</th>
                        <th className="table-header-cell">Custody Fee</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {selectedReportData.topInvestorAccounts.map(
                        (account, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {account.accountId}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">
                              {account.investorName}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-gray-900">
                              RM {account.totalHoldings.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-right text-success-600">
                              RM {account.accruedProfit.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm font-medium text-right text-purple-600">
                              RM {account.custodyFee.toLocaleString()}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Daily Position Table - for Daily Position Report */}
            {selectedReportData.positions && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Daily Position Details
                </h3>
                <div className="overflow-x-auto">
                  <table className="table text-xs border border-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Normal Master No</th>
                        <th className="table-header-cell">
                          Normal Master Name
                        </th>
                        <th className="table-header-cell">Master No</th>
                        <th className="table-header-cell">Master Name</th>
                        <th className="table-header-cell">
                          Custody Account No
                        </th>
                        <th className="table-header-cell">
                          Custody Account Name
                        </th>
                        <th className="table-header-cell">ISIN</th>
                        <th className="table-header-cell">Scan Code</th>
                        <th className="table-header-cell">Security Code</th>
                        <th className="table-header-cell">
                          Security Description
                        </th>
                        <th className="table-header-cell">Security Type</th>
                        <th className="table-header-cell">Security Status</th>
                        <th className="table-header-cell">Bond Type</th>
                        <th className="table-header-cell">Location Code</th>
                        <th className="table-header-cell">Currency</th>
                        <th className="table-header-cell">Account Type</th>
                        <th className="table-header-cell">Scripless Flag</th>
                        <th className="table-header-cell">Maturity Date</th>
                        <th className="table-header-cell">Position Date</th>
                        <th className="table-header-cell">
                          Safekeeping Position
                        </th>
                        <th className="table-header-cell">Wallet No</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {selectedReportData.positions.map((position, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-2 py-2 font-mono text-xs text-gray-900">
                            {position.normalMasterNo}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.normalMasterName}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-gray-900">
                            {position.masterNo}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.masterName}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-gray-900">
                            {position.custodyAccountNo}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.custodyAccountName}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-gray-900">
                            {position.isin}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-gray-900">
                            {position.scanCode}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-gray-900">
                            {position.securityCode}
                          </td>
                          <td className="max-w-xs px-2 py-2 text-xs text-gray-900">
                            <div className="truncate">
                              {position.securityDescription}
                            </div>
                          </td>
                          <td className="px-2 py-2 text-xs">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                position.securityType === "SUKUK"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {position.securityType}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.securityStatus}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.productType}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.locationCode}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.currency}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.accountType}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.scriplessFlag}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {position.maturityDate}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-900">
                            {selectedReportData.positionDate}
                          </td>
                          <td className="px-2 py-2 text-xs font-medium text-right text-gray-900">
                            {position.safekeepingPosition}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-gray-900">
                            {position.walletNo}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Transaction File Table - for Transaction File Report */}
            {selectedReportData.transactions && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Transaction File Details
                </h3>
                <div className="overflow-x-auto">
                  <table className="table text-xs border border-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Master Account No</th>
                        <th className="table-header-cell">
                          Master Account Name
                        </th>
                        <th className="table-header-cell">
                          Custody Account No
                        </th>
                        <th className="table-header-cell">
                          Custody Account Name
                        </th>
                        <th className="table-header-cell">Security Code</th>
                        <th className="table-header-cell">Security Name</th>
                        <th className="table-header-cell">Security Location</th>
                        <th className="table-header-cell">Currency</th>
                        <th className="table-header-cell">Trade Date</th>
                        <th className="table-header-cell">Settlement Date</th>
                        <th className="table-header-cell">Settlement Amount</th>
                        <th className="table-header-cell">Quantity</th>
                        <th className="table-header-cell">System Ref No</th>
                        <th className="table-header-cell">Client Ref No</th>
                        <th className="table-header-cell">Counterparty Code</th>
                        <th className="table-header-cell">Counterparty Name</th>
                        <th className="table-header-cell">Cash Acc No</th>
                        <th className="table-header-cell">Inputter</th>
                        <th className="table-header-cell">Input Date</th>
                        <th className="table-header-cell">Authorizer</th>
                        <th className="table-header-cell">Authorize Date</th>
                        <th className="table-header-cell">Billing Tag</th>
                        <th className="table-header-cell">Scripless Flag</th>
                        <th className="table-header-cell">Trade Status</th>
                        <th className="table-header-cell">Wallet No</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {selectedReportData.transactions.map(
                        (transaction, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {transaction.masterAccountNo}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.masterAccountName}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {transaction.custodyAccountNo}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.custodyAccountName}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {transaction.securityCode}
                            </td>
                            <td className="max-w-xs px-2 py-2 text-xs text-gray-900">
                              <div className="truncate">
                                {transaction.securityName}
                              </div>
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.securityLocation}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.currency}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.tradeDate}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.settlementDate}
                            </td>
                            <td className="px-2 py-2 text-xs font-medium text-right text-gray-900">
                              {transaction.settlementAmount}
                            </td>
                            <td className="px-2 py-2 text-xs text-right text-gray-900">
                              {transaction.quantity}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {transaction.uniqueRefNum}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {transaction.refNo}
                            </td>
                            <td className="max-w-xs px-2 py-2 text-xs text-gray-900">
                              <div className="truncate">
                                {transaction.counterparty}
                              </div>
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.counterpartyName}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.cashAccount}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {transaction.maker}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.makerDate}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.checker}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.checkerDate}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  transaction.tradeStatus === "COMPLETED"
                                    ? "bg-green-100 text-green-800"
                                    : transaction.tradeStatus === "PENDING"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {transaction.nonBillable}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {transaction.scriptlessFlag}
                            </td>
                            <td className="px-2 py-2 text-xs">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  transaction.tradeStatus === "COMPLETED"
                                    ? "bg-green-100 text-green-800"
                                    : transaction.tradeStatus === "PENDING"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {transaction.tradeStatus}
                              </span>
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {transaction.walletNo}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Trade Instruction File Table - for Trade Instruction File Report */}
            {selectedReportData.tradeInstructions && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Trade Instruction File Details
                </h3>
                <div className="overflow-x-auto">
                  <table className="table text-xs border border-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Trade Type</th>
                        <th className="table-header-cell">Custody AC No</th>
                        <th className="table-header-cell">Scan Code</th>
                        <th className="table-header-cell">Stamp Duty</th>
                        <th className="table-header-cell">ISIN Code</th>
                        <th className="table-header-cell">Client Ref No</th>
                        <th className="table-header-cell">ISS Tag</th>
                        <th className="table-header-cell">Trade Date</th>
                        <th className="table-header-cell">Value Date</th>
                        <th className="table-header-cell">No Of Shares</th>
                        <th className="table-header-cell">Settlement Amount</th>
                        <th className="table-header-cell">
                          Counterparty ADA Code
                        </th>
                        <th className="table-header-cell">Clearing Fee</th>
                        <th className="table-header-cell">Clearing Fee GST</th>
                        <th className="table-header-cell">Buy/Sell Price</th>
                        <th className="table-header-cell">Brokerage Fee</th>
                        <th className="table-header-cell">Brokerage Fee GST</th>
                        <th className="table-header-cell">SSA Account No</th>
                        <th className="table-header-cell">Accrued Interest</th>
                        <th className="table-header-cell">Turnaround Flag</th>
                        <th className="table-header-cell">Wallet No</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {selectedReportData.tradeInstructions.map(
                        (instruction, index) => (
                          <tr key={index} className="border-t border-gray-200">
                            <td className="px-2 py-2 text-xs">
                              {(() => {
                                const tt = (
                                  instruction.tradeType || ""
                                ).toLowerCase();
                                const isBuy = tt === "b" || tt === "buy";
                                const isSell = tt === "s" || tt === "sell";
                                const classes = isBuy
                                  ? "bg-green-100 text-green-800"
                                  : isSell
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800";
                                return (
                                  <span
                                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${classes}`}
                                  >
                                    {instruction.tradeType}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {instruction.custodyAccountNo}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {instruction.scanCode}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {instruction.stampDuty}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {instruction.isinCode}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {instruction.clientRefNo}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {instruction.issTag}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {instruction.tradeDate}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {instruction.valueDate}
                            </td>
                            <td className="px-2 py-2 text-xs text-right text-gray-900">
                              {instruction.noOfShares}
                            </td>
                            <td className="px-2 py-2 text-xs font-medium text-right text-gray-900">
                              {instruction.settlementAmount}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {instruction.counterpartyAdaCode}
                            </td>
                            <td className="px-2 py-2 text-xs text-right text-gray-900">
                              {instruction.clearingFee}
                            </td>
                            <td className="px-2 py-2 text-xs text-right text-gray-900">
                              {instruction.clearingFeeGst}
                            </td>
                            <td className="px-2 py-2 text-xs text-right text-gray-900">
                              {instruction.buySellPrice}
                            </td>
                            <td className="px-2 py-2 text-xs text-right text-gray-900">
                              {instruction.brokerageFee}
                            </td>
                            <td className="px-2 py-2 text-xs text-right text-gray-900">
                              {instruction.brokerageFeeGst}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {instruction.ssaAccountNo}
                            </td>
                            <td className="px-2 py-2 text-xs text-right text-gray-900">
                              {instruction.accruedInterest}
                            </td>
                            <td className="px-2 py-2 text-xs text-gray-900">
                              {instruction.turnaroundFlag}
                            </td>
                            <td className="px-2 py-2 font-mono text-xs text-gray-900">
                              {instruction.walletNo}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Summary Display */}
            {reportSummary && (
              <div className="p-4 mt-6 border border-purple-200 rounded-lg bg-purple-50">
                <h3 className="flex items-center mb-3 text-lg font-semibold text-purple-900">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  AI-Generated Report Summary
                </h3>
                <div className="prose-sm prose text-gray-700 whitespace-pre-wrap max-w-none">
                  {reportSummary}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportSummary(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleGenerateSummary}
                disabled={isGeneratingSummary}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed"
              >
                {isGeneratingSummary ? (
                  <>
                    <RefreshCw className="inline w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <BarChart3 className="inline w-4 h-4 mr-2" />
                    Generate Summary
                  </>
                )}
              </button>
              <button
                onClick={() => handleDownloadCurrentReport("csv")}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
              >
                <Download className="inline w-4 h-4 mr-2" />
                Download CSV
              </button>
              <button
                onClick={() => handleDownloadCurrentReport("pdf")}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-primary-600 hover:bg-primary-700"
              >
                <Download className="inline w-4 h-4 mr-2" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustodianReporting;
